import React, { useState, useEffect, useCallback, useId } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
import { useTranslation } from 'react-i18next';
import { useToast } from './ToastContext';
import { GOLD, NAVY_FORM } from './colors';
import { LoadError, ScrollableTable, useIsNarrow, gridCols } from './ui';
import { blobErrorDetail, previewBlob, saveBlob } from './downloadFile';
// FPTAB: the employee's own F&P questionnaire, now a tab of this page.
import FPEmployeeSection from './FPEmployeeSection';
import { FP_NOTICE_ROLES } from './FPAssessmentShared';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate, todayISO } from './dateOnly';

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

const fmtApiError = (e) => {
  const detail = e.response?.data?.detail;
  if (!detail) return e.message || 'Error saving';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => {
    const loc = d.loc ? d.loc.filter(x => x !== 'body').join('.') : '';
    return loc ? `${loc}: ${d.msg}` : d.msg;
  }).join(' | ');
  return JSON.stringify(detail);
};

// COMPVIEWFIX (Holly's PAD feedback): added GBX (pence sterling, how many LSE equities are priced)
// and AUD, plus the other major currencies that appear in the real historical PAD data (CHF, JPY,
// CAD, HKD, SGD) so a trade can always be recorded in its actual currency. GBP/USD/EUR/ZAR/XOF/XAF/
// MUR/RWF were the original set and are unchanged.
const CURRENCIES = [
  'GBP', 'GBX', 'USD', 'EUR', 'AUD', 'CHF', 'JPY', 'CAD', 'HKD', 'SGD',
  'ZAR', 'XOF', 'XAF', 'MUR', 'RWF',
];

const nullDates = (obj, ...keys) => {
  const out = { ...obj };
  keys.forEach(k => { if (!out[k]) out[k] = null; });
  return out;
};

// Normalise any date string to ISO YYYY-MM-DD, or null if empty/invalid.
// Handles both "YYYY-MM-DD" (HTML spec) and "DD/MM/YYYY" (Chrome UK locale quirk).
const toISODate = (v) => {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [d, m, y] = v.split('/');
    return `${y}-${m}-${d}`;
  }
  return null;
};

// ─── Shared helpers ───────────────────────────────────────

const fmt = (d) => (d ? toDisplayDate(d).toLocaleDateString('en-GB') : '-');

// DECLDELAY: the two columns the four declaration tabs now carry, rendered the same way in all four.
//
// days_to_report is computed SERVER SIDE by the registers' own _reporting_delay_days, so the tab and
// the register can never disagree about the same declaration. The client only formats it.
//
// ZERO IS A REAL VALUE AND MUST RENDER AS "0", not as an empty cell: same day is on time, which is
// the whole point of the number. So the test is `== null`, never a falsy check, which would have
// swallowed 0 and silently turned the best possible delay into a blank.
//
// null RENDERS AS AN EMPTY CELL, never as 0 or a dash-with-a-number. The server returns null in the
// two cases where no honest figure exists (no event date, or an event later than the submission), and
// an empty cell is a question while a number would be a false answer.
const fmtDelay = (days) => (days == null ? '' : String(days));

// EDITBYNAME: the edit history shows the editor's NAME, never their email address.
//
// THE DEFECT: this rendered "by {entry.edited_by}", and edited_by stores the authenticated editor's
// EMAIL - so every entry printed an address where a name belongs. Measured on dev: every stored value
// is a bare email.
//
// `names` is the server's derived {stored value: resolved name} map, built by the SAME COMPBYNAME
// resolver that already names the Section 4 completer and the declaration reviewer. It returns null
// rather than the email for a value it cannot resolve, so there is no path by which an address reaches
// this component as a "name". A null falls back to the NEUTRAL LABEL below, never to entry.edited_by:
// showing the address instead is exactly the leak this closes.
function EditLog({ history, names, t }) {
  const log = (() => { try { return JSON.parse(history || '[]'); } catch { return []; } })();
  if (!log.length) return null;
  const who = (stored) => {
    const resolved = names && stored ? names[stored] : null;
    if (resolved) return resolved;
    // The same neutral label NAMERES uses for an unresolvable employee, so the two read alike. The
    // optional `t` keeps this component renderable without a translator, as it was before.
    return t ? t('compliance.unknownEmployee', 'Unknown employee') : 'Unknown employee';
  };
  return (
    <div style={s.editLog}>
      <p style={s.editLogTitle}>Edit history</p>
      {log.map((entry, i) => (
        <div key={i} style={s.editLogEntry}>
          <span style={s.editLogMeta}>{toDisplayDate(entry.timestamp).toLocaleString('en-GB')} by {who(entry.edited_by)}</span>
          {Object.entries(entry.changes).map(([field, { old: oldVal, new: newVal }]) => (
            <span key={field} style={s.editLogChange}>
              {field}: "{oldVal}" → "{newVal}"
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

// COMPREVIEW: structured compliance review panel for a declaration row. declType is the URL
// segment ('outside-affiliations' / 'gifts' / 'pad' / 'public-officials'). Shows a read-only
// summary once reviewed (rec.reviewed_at set), else inputs (compliance role only - the caller
// gates rendering on canChangeStatus). onReviewed refreshes the parent list.
function ReviewPanel({ declType, rec, onReviewed }) {
  const showToast = useToast();
  const [questions, setQuestions] = useState(null);
  const [c1, setC1] = useState('');
  const [c1Notes, setC1Notes] = useState('');
  const [c2, setC2] = useState('');
  const [c2Notes, setC2Notes] = useState('');
  const [comments, setComments] = useState('');
  const [decision, setDecision] = useState('');
  const [saving, setSaving] = useState(false);
  // LOADERR: failed question load rendered as an error, not as a silently degraded panel.
  const [questionsFailed, setQuestionsFailed] = useState(false);
  const reviewed = !!rec.reviewed_at;

  // PADPANEL: fetched for a REVIEWED record too, which it previously was not (`if (reviewed)
  // return;`). The reviewed panel shows each recorded answer beside the question it answers, so it
  // needs the same wording the form uses. Without this the reviewed panel could only ever print
  // the bare labels "Q1:" and "Q2:".
  //
  // The endpoint is open to any authenticated user (it returns label text only, no record data),
  // and a failure is non-fatal here: the reviewed panel falls back to a neutral "Review question 1"
  // label rather than showing an error, because the ANSWER is the content and it is already in
  // hand. questionsFailed still drives the form's own error, whose behaviour is unchanged.
  useEffect(() => {
    axios.get(`${API}/compliance/review-questions/${declType}`)
      .then(r => { setQuestions(r.data); setQuestionsFailed(false); })
      .catch(() => { setQuestions(null); setQuestionsFailed(true); });
  }, [declType]);

  const submit = async () => {
    if (c1 !== 'Yes' && c1 !== 'No') { showToast('Answer the first review question.', 'error'); return; }
    if (c2 !== 'Yes' && c2 !== 'No') { showToast('Answer the second review question.', 'error'); return; }
    if (!decision.trim()) { showToast('Select a review decision.', 'error'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/compliance/${declType}/${rec.id}/review`, {
        c1_choice: c1, c1_notes: c1Notes || null, c2_choice: c2, c2_notes: c2Notes || null,
        comments: comments || null, decision,
      });
      showToast('Review recorded', 'success');
      onReviewed && onReviewed();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Could not record review', 'error');
    } finally { setSaving(false); }
  };

  if (reviewed) {
    return (
      <div style={{ background: '#f0f4f8', borderLeft: `3px solid ${NAVY_FORM}`, borderRadius: 6, padding: '10px 14px', margin: '4px 0', fontSize: 12.5 }}>
        {/* PADPANEL: the reviewer's NAME, never the stored email. reviewed_by_name is resolved
            server-side and is null rather than an address when it cannot be resolved, so the
            neutral label is what renders instead. */}
        <p style={{ margin: '0 0 6px', fontWeight: 600, color: NAVY_FORM }}>
          Compliance review - {rec.review_decision}
          <span style={{ fontWeight: 400, color: '#888' }}>
            {' '}by {reviewerName(rec)} on {toDisplayDate(rec.reviewed_at).toLocaleDateString('en-GB')}
          </span>
        </p>
        {/* PADPANEL: the review answers, with the QUESTION each one answers.
            THE DEFECT THIS CLOSES: these rendered as bare "Q1:" and "Q2:" with nothing after them.
            Two separate causes, both fixed. First, the labels were the literal strings "Q1"/"Q2",
            which name nothing: the real wording lives in the review-questions endpoint and is
            already shown on the form the reviewer fills in. Second, the questions were never
            FETCHED once a record was reviewed (`if (reviewed) return;` in the effect below), so
            even the wording that existed was unavailable here.
            An answer with no choice recorded is HIDDEN entirely rather than shown empty: a label
            with nothing after it tells the reader less than no line at all. */}
        {rec.review_c1_choice && (
          <div>{questions?.c1_question || 'Review question 1'}: {rec.review_c1_choice}
            {rec.review_c1_notes ? ` - ${rec.review_c1_notes}` : ''}</div>
        )}
        {rec.review_c2_choice && (
          <div>{questions?.c2_question || 'Review question 2'}: {rec.review_c2_choice}
            {rec.review_c2_notes ? ` - ${rec.review_c2_notes}` : ''}</div>
        )}
        {rec.review_comments && <div style={{ marginTop: 4 }}>Comments: {rec.review_comments}</div>}
      </div>
    );
  }

  const yn = (val, setter) => (
    <select value={val} onChange={e => setter(e.target.value)}
      style={{ fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '1px solid #ddd' }}>
      <option value="">Select…</option><option value="Yes">Yes</option><option value="No">No</option>
    </select>
  );
  const noteInput = (val, setter) => (
    <input type="text" value={val} onChange={e => setter(e.target.value)} placeholder="Notes (optional)"
      style={{ fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '1px solid #ddd', width: 240 }} />
  );

  return (
    <div style={{ background: '#fffdf5', border: '1px solid #e8dfc0', borderRadius: 6, padding: '10px 14px', margin: '4px 0', fontSize: 12.5 }}>
      <p style={{ margin: '0 0 8px', fontWeight: 600, color: NAVY_FORM }}>Compliance review</p>
      {questionsFailed && (
        <div style={{ marginBottom: 8 }}>
          <LoadError message="Couldn't load the review questions." />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>
          <div style={{ marginBottom: 3 }}>{questions?.c1_question || 'Loading question…'}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{yn(c1, setC1)}{noteInput(c1Notes, setC1Notes)}</div>
        </div>
        <div>
          <div style={{ marginBottom: 3 }}>{questions?.c2_question || 'Loading question…'}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{yn(c2, setC2)}{noteInput(c2Notes, setC2Notes)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Decision:</span>
          <select value={decision} onChange={e => setDecision(e.target.value)}
            style={{ fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '1px solid #ddd' }}>
            <option value="">Select…</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Escalated">Escalated</option>
          </select>
          <input type="text" value={comments} onChange={e => setComments(e.target.value)}
            placeholder="Overall comments (optional)"
            style={{ fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '1px solid #ddd', width: 260 }} />
          <button onClick={submit} disabled={saving}
            style={{ background: NAVY_FORM, color: '#fff', border: 'none', borderRadius: 4, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'Saving…' : 'Record review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// COMPVIEWFIX: full submitted-details panel for the compliance review view. The per-tab tables
// show a curated subset of columns; this renders EVERY field the employee submitted so a compliance
// reviewer (Holly) can see the complete declaration in one place, including the booleans/fields the
// tables omit (is_competitor/is_paid_employment for OA; purpose for G&E; the trade-confirmation
// document link for PAD). Rendered only in the compliance view (callers gate on canChangeStatus),
// just above each ReviewPanel. Reuses the ReviewPanel's own boxed look. `rows` is a list of
// [label, value] pairs, already resolved by the caller; falsy values are shown as a muted dash.
// COMPMODAL (confirmed COO decision, refining COMPCOLLAPSE's inline collapsible): the full
// submitted details now open in a MODAL OVERLAY on top of the page instead of expanding inline
// below the table row. The reviewer clicks "View" on a declaration and the modal shows every
// submitted field in one place, with the ReviewPanel INSIDE the modal beneath the details (see the
// checklist entry for why: review-while-viewing keeps everything in one place, which is the whole
// point of the modal - splitting details into the modal but leaving review in the row would
// re-create the two-places friction). Reuses the exact overlay/modal visual pattern from
// DocumentUpload.js (fixed full-screen backdrop rgba(0,0,0,0.5), centred white card, radius 12,
// header with title + close X). Closes on the X button AND on backdrop click, matching standard
// modal UX. `rows` is a list of [label, value] pairs already resolved by the caller; undefined
// entries are dropped, null/'' shown as a muted dash. `title`/`subtitle` name the declaration;
// `children` is the ReviewPanel. Rendering nothing when !rec keeps each caller's markup trivial.
function DeclarationDetailsModal({ rec, title, subtitle, rows, onClose, children }) {
  // RESPONSIVE Phase 4: the label/value detail grid uses a 'max-content 1fr' template whose
  // max-content label column, plus a long value, can overflow the modal horizontally when narrow.
  // Below ~900px it reflows to a single column (gridCols -> '1fr'), so each label/value fragment
  // stacks (label on its own row, value below) instead of overflowing. Hook is called before the
  // early return below so it is unconditional.
  const isNarrow = useIsNarrow();
  if (!rec) return null;
  const visible = rows.filter(([, v]) => v !== undefined);
  return (
    <div style={sM.overlay} onClick={onClose}>
      <div style={sM.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={sM.header}>
          <div>
            <h2 style={sM.title}>{title}</h2>
            {subtitle && <p style={sM.subtitle}>{subtitle}</p>}
          </div>
          <button style={sM.closeBtn} onClick={onClose} aria-label="Close">&#10005;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'max-content 1fr'), gap: '6px 16px',
                      alignItems: 'baseline', fontSize: 13 }}>
          {visible.map(([label, value]) => (
            <React.Fragment key={label}>
              <span style={{ color: '#667', fontWeight: 500 }}>{label}</span>
              <span style={{ color: '#222' }}>
                {value === null || value === '' ? <span style={{ color: '#aaa' }}>-</span> : value}
              </span>
            </React.Fragment>
          ))}
        </div>
        {children && <div style={{ marginTop: 20 }}>{children}</div>}
      </div>
    </div>
  );
}

// COMPMODAL: modal styles, matching DocumentUpload.js's overlay/modal pattern (kept local to this
// module so the compliance modal is self-contained). The card is a touch wider and scrolls
// vertically because a full declaration + its ReviewPanel is taller than the upload dialog.
const sM = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    backgroundColor: '#fff', borderRadius: 12,
    padding: 28, width: 640, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 20,
  },
  title: { fontSize: 18, fontWeight: 600, color: '#1a1a2e', margin: 0 },
  subtitle: { fontSize: 14, color: '#666', margin: '4px 0 0 0' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 18,
    cursor: 'pointer', color: '#666', padding: 0, lineHeight: 1,
  },
  viewBtn: {
    padding: '4px 12px', border: '1px solid ' + NAVY_FORM, borderRadius: 4,
    background: '#fff', color: NAVY_FORM, fontSize: 12, cursor: 'pointer', fontWeight: 500,
  },
};

// PADATTACH: fetch a PAD attachment through the portal's own scoped route instead of linking at
// the raw SharePoint URL.
//
// THE STATE BEFORE (COMPVIEWFIX): this rendered a bare <a href={path}> at the stored
// confirmation_file_path, which in production is the raw SharePoint webUrl. The click left the
// portal entirely, carried NO bearer token, and landed somewhere access depended on the viewer's
// own SharePoint permissions rather than their portal role - so a compliance officer without
// direct access to the Compliance site could not open a file the portal was showing them. A row
// with a name but no path rendered as dead, unclickable text with no explanation at all.
//
// REUSES PayrollAttachmentLink's shape deliberately (WorkflowList.js): read meta, then fetch the
// bytes as a BLOB through axios so the Authorization header travels with the request, offer
// Preview only where the SERVER will serve inline and Download always, and revoke the object URL
// on the same 60s delay rather than immediately (the new tab still needs it to resolve after the
// handler returns). No second mechanism is invented here.
function PadAttachmentLink({ recordId, slot, name }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recordId || !slot) return undefined;
    let live = true;
    axios.get(`${API}/compliance/pad/${recordId}/attachment/${slot}/meta`)
      .then(res => { if (live) setMeta(res.data || null); })
      // A failed metadata read must not hide the attachment: the download control below falls
      // back to the name we were handed, so the file stays reachable.
      .catch(() => { if (live) setMeta(null); });
    return () => { live = false; };
  }, [recordId, slot]);

  // Nothing in this slot at all.
  if (!name && !recordId) return <span style={{ color: '#aaa' }}>-</span>;

  // DLNAME: withBlob now hands the RESPONSE to its callback rather than a blob URL, because the
  // download name lives in that response's X-Download-Filename header. The previous shape created
  // the object URL inside fetchBlob and threw the response away, so the only name still reachable
  // at download time was the client's own `meta.file_name` copy. That was a SECOND mechanism: the
  // server and the browser could disagree about what a file is called. Both paths now go through
  // the shared helper, which is the single implementation.
  const withBlob = async (use) => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/compliance/pad/${recordId}/attachment/${slot}`, { responseType: 'blob' });
      use(res);
    } catch (e) {
      // PADPREVIEW: the server's own sentence, read through the shared helper. With
      // responseType 'blob' the error body is a Blob, so `e.response.data.detail` was always
      // undefined and the generic fallback was all anyone ever saw. The message lands INSIDE this
      // panel: the global overlay no longer fires for this path, so the officer keeps her place.
      setError(await blobErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  // Preview behaviour is unchanged: a blob opened in a tab has no filename mechanism at all, which
  // is exactly why a separate Download control sits beside it.
  const openPreview = () => withBlob((res) => previewBlob(res));

  const download = () => withBlob((res) => saveBlob(res, name || 'Document'));

  const linkStyle = {
    fontSize: 12, color: NAVY_FORM, textDecoration: 'underline', marginLeft: 6,
    background: 'none', border: 'none', padding: 0, cursor: busy ? 'wait' : 'pointer',
  };

  const shownName = (meta && meta.file_name) || name;
  if (!shownName) return <span style={{ color: '#aaa' }}>-</span>;

  // THE NULL-PATH CASE, which previously rendered as dead text: the row carries a file NAME but
  // nothing retrievable behind it. Say so plainly rather than offering controls that would 404.
  if (meta && meta.retrievable === false) {
    return (
      <span>
        {shownName}
        <span style={{ fontSize: 12, color: '#8a6a1f', marginLeft: 6 }}>
          File not available in storage - contact Compliance.
        </span>
      </span>
    );
  }

  return (
    <span>
      {shownName}
      {meta && meta.can_preview ? (
        <button type="button" onClick={openPreview} disabled={busy} style={linkStyle}>
          Preview
        </button>
      ) : null}
      <button type="button" onClick={download} disabled={busy} style={linkStyle}>
        Download
      </button>
      {error ? (
        <span style={{ fontSize: 12, color: '#A63A3A', marginLeft: 6 }}>{error}</span>
      ) : null}
    </span>
  );
}

const _yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);

// PADPANEL: a NAME is shown where a name belongs, never a raw email or id.
//
// THE DEFECT THIS CLOSES: the detail panel rendered `submitted_by` directly, which stores the
// submitter's email address, so every declaration detail showed an address where the rest of the
// portal shows a person.
//
// employee_name is the server's own resolved display name for the declaration's subject, already
// served with every row (ROWNAME) from a lookup that includes archived and deactivated employees,
// so a declaration stays attributable after the person has left.
//
// THE EMAIL IS NEVER THE FALLBACK. When no name can be resolved the neutral label is shown, which
// is the same rule COMPBYNAME applies everywhere else: showing the address instead is exactly the
// leak that rule exists to prevent.
const UNKNOWN_PERSON = 'Unknown employee';
export const submitterName = (rec) => {
  const name = (rec?.employee_name || '').trim();
  if (name && !name.includes('@')) return name;
  return UNKNOWN_PERSON;
};

// PADPANEL: the reviewer's name for the compliance review line. reviewed_by_name is resolved
// server-side by the COMPBYNAME resolver and is null rather than an email when it cannot be
// resolved, so the neutral label below is what a missing name renders as.
const UNKNOWN_REVIEWER = 'a compliance officer';
export const reviewerName = (rec) => {
  const name = (rec?.reviewed_by_name || '').trim();
  if (name && !name.includes('@')) return name;
  return UNKNOWN_REVIEWER;
};

function Spinner() {
  return <span style={s.spinner} />;
}

function PolicyLink({ category }) {
  const [policyId, setPolicyId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/policies/`)
      .then(res => {
        const match = (res.data || []).find(p => p.category === category);
        if (match) setPolicyId(match.id);
      })
      .catch(() => {});
  }, [category]);

  // POLICYNAME: same fix as PolicyRepository's own openPolicy. The anchor had no `download`
  // attribute, so the browser named the saved file after the blob: URL's last segment, a generated
  // UUID that read as the policy id. The name is taken from the SERVER's X-Download-Filename
  // header, so this site and the repository cannot drift apart on what a policy is called.
  const openPolicy = async () => {
    if (!policyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/policies/${policyId}/view`, { responseType: 'blob' });
      // DLNAME: the shared helper, so this site and the repository cannot drift apart on what a
      // policy is called. The name comes from the server's X-Download-Filename header.
      saveBlob(res, 'Policy');
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  if (!policyId) return null;
  return (
    <button onClick={openPolicy} disabled={loading}
      style={{ background: 'none', border: 'none', color: NAVY_FORM, textDecoration: 'underline',
               fontSize: 13, cursor: 'pointer', padding: 0, fontStyle: 'italic' }}>
      {loading ? 'Opening…' : 'View policy'}
    </button>
  );
}

// ─── Tab: Outside Affiliations ───────────────────────────

const POSITION_OPTIONS = [
  'Director',
  'Majority shareholder/controller',
  'Trustee',
  'Governmental official',
  'Other',
];

function statusBadge(status, t) {
  const map = {
    'Pending':   { bg: '#FEF3C7', color: '#92400E' },
    'Approved':  { bg: '#D1FAE5', color: '#065F46' },
    'Rejected':  { bg: '#FEE2E2', color: '#991B1B' },
    'Ceased':    { bg: '#F3F4F6', color: '#6B7280' },
    // ESCALSYNC: a distinct deep-orange/amber for "Escalated" - reads as "flagged, needs further
    // attention", clearly apart from Pending's pale yellow (passive wait), Rejected's red (final
    // negative verdict) and Ceased's grey.
    'Escalated': { bg: '#FFEDD5', color: '#9A3412' },
  };
  const st = map[status] || { bg: '#F3F4F6', color: '#374151' };
  const label = status && t
    ? t(`compliance.status.${status.toLowerCase()}`, status)
    : (t ? t('compliance.status.pending') : 'Pending');
  return (
    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: st.bg, color: st.color }}>
      {label}
    </span>
  );
}

// NAMERES: the ONE employee-name resolver the four declaration tabs share.
// ─────────────────────────────────────────────────────────────────────────────
//
// THE STATE BEFORE: each of the four tabs had its own lookup, and all four fell back to the RAW
// employee_id. OutsideAffiliationsTab had `empName()`; the other three repeated the same
// `employees.find(...)` expression inline, three times each in some cases, ending `: rec.employee_id`.
// So an unresolvable row printed a UUID on screen, the same defect ATTESTNOID removed from the
// Attestations tab.
//
// CAN THAT STILL HAPPEN NOW THAT EVERY LIST IS SCOPED SERVER SIDE? YES, and it was measured
// rather than assumed. The declaration endpoints return a row for an employee whatever their
// lifecycle state, but the employee PICKER (GET /employees/) defaults to status="active", which
// filters `is_archived == False AND is_active == True`. So two cases survive:
//   * an ARCHIVED (offboarded) employee, whose declarations correctly stay on file; and
//   * a DEACTIVATED employee.
// Proven by execution: a UK-scoped officer receives 3 declaration rows while the picker returns
// 2 employees, leaving 2 rows with no resolvable name. See verify_nameres.py N1.
//
// A third, transient case: the picker fetch can fail (pickerFailed) or simply not have resolved
// yet, which leaves `employees` empty for a moment.
//
// In every one of those cases the honest answer is a neutral label, never an id. An id tells the
// reader nothing, and it is a value they should not have to see.
//
// Takes `t` rather than hardcoding the label, so it reads "Unknown employee" / "Employe inconnu"
// in the account's own language.
// DECLENTITY: the entity sub-line under an employee name, shared by the four declaration tabs.
//
// ONE COMPONENT rather than the same JSX four times: the Tasks list established this treatment
// (SAMENAMEENTITY) and the four tabs must match it and each other, so there is one place to change.
//
// THE ENTITY IS RESOLVED SERVER SIDE and read straight off the row. It is never derived here from an
// employee lookup: a client-side search of the employee picker loses the label for an ARCHIVED
// employee, which is the lifecycle gap ROWNAME closed for the name itself.
//
// AN ABSENT ENTITY RENDERS NOTHING, not a dash and not a placeholder. The server sends null when the
// value is missing or unresolvable, and inventing a label would claim something that is not known.
function EntityLine({ entity }) {
  if (!entity) return null;
  return (
    <div style={{ fontSize: '11px', color: '#777', marginTop: '1px' }}>{entity}</div>
  );
}

function resolveEmployeeName(employees, id, t, row) {
  // ROWNAME: THE SERVER'S OWN NAME FIRST. The four list endpoints now send `employee_name` on
  // every row, derived from the same source the registers' CSV exports use, which includes
  // ARCHIVED and DEACTIVATED employees. The picker this function searches below is limited to
  // ACTIVE employees, so it cannot name somebody who has left: that is exactly the case that
  // displayed the neutral label, and the server is the only party that can answer it.
  const sent = row && typeof row.employee_name === 'string' ? row.employee_name.trim() : '';
  if (sent) return sent;
  // Then the picker, which still answers for any row served without a name (an older client
  // cache, or any consumer that does not set the field).
  const e = (employees || []).find(x => x.id === id);
  if (e) return `${e.first_name} ${e.last_name}`;
  // And the neutral label ONLY when both are missing. Never a raw id.
  return t('compliance.unknownEmployee');
}

function OutsideAffiliationsTab({ entityScope, currentUser, employeeId, isAdmin, employees, readOnly, canChangeStatus }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const showToast = useToast();
  // RESPONSIVE Phase 2: two-column field rows reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...s.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ceaseId, setCeaseId] = useState(null);
  const [ceaseDate, setCeaseDate] = useState('');
  const [detailsRec, setDetailsRec] = useState(null);  // COMPMODAL: declaration open in the modal

  const blank = {
    affiliated_entity: '', nature_activities: '', position: '', position_details: '',
    date_commenced: '', additional_information: '',
    is_competitor: false, is_paid_employment: false, is_none_of_above: false,
  };
  const [form, setForm] = useState(blank);

  // LOADERR: distinguishes a FAILED register load from a genuinely empty one.
  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const params = isAdmin
      ? (filterEmp ? { employee_id: filterEmp } : {})
      : { employee_id: employeeId };
    axios.get(`${API}/compliance/outside-affiliations`, { params })
      .then(res => {
        // COMPSCOPEUI: show only rows this account may actually reach. `employees` is the
        // already-scoped list, so a record whose employee is not in it is out of scope and
        // the backend would refuse it anyway. Unscoped (entityScope null) keeps every row,
        // so coo, admin and the CCO see exactly what they saw before.
        const rows = res.data || [];
        const allowed = new Set((employees || []).map(e => e.id));
        setRecords(entityScope ? rows.filter(r => allowed.has(r.employee_id)) : rows);
        setLoadFailed(false);
      })
      // LOADERR: a failed load must not render as "no declarations".
      .catch(() => { setRecords([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [isAdmin, employeeId, filterEmp, entityScope, employees]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (rec, newStatus) => {
    try {
      await axios.put(`${API}/compliance/outside-affiliations/${rec.id}`, {
        status: newStatus, edited_by: currentUser,
      });
      load();
    } catch (e) { showToast(fmtApiError(e), 'error'); }
  };

  const handleCease = async () => {
    if (!ceaseDate) { return; }
    try {
      // CEASEREVERSE: compliance staff also flip status to "Ceased"; the declaration owner
      // (self-amend) may only set date_ceased on their own Pending record - status stays
      // admin-only server-side, so sending it as the owner would 403. Send date_ceased alone.
      const payload = canChangeStatus
        ? { date_ceased: ceaseDate, status: 'Ceased', edited_by: currentUser }
        : { date_ceased: ceaseDate, edited_by: currentUser };
      await axios.put(`${API}/compliance/outside-affiliations/${ceaseId}`, payload);
      setCeaseId(null); setCeaseDate(''); load();
    } catch (e) { showToast(fmtApiError(e), 'error'); }
  };

  const submit = async () => {
    if (!form.affiliated_entity) { setError('Affiliated Entity is required.'); return; }
    if (!form.nature_activities) { setError('Nature / Activities is required.'); return; }
    if (!form.position) { setError('Position is required.'); return; }
    if ((form.position === 'Other' || form.position === 'Governmental official') && !form.position_details) {
      setError('Please provide details for the selected position.'); return;
    }
    if (!form.date_commenced) { setError('Date Commenced is required.'); return; }
    if (!form.is_competitor && !form.is_paid_employment && !form.is_none_of_above) {
      setError('Please tick at least one checkbox.'); return;
    }
    setSaving(true); setError(null);
    try {
      if (editingId) {
        await axios.put(`${API}/compliance/outside-affiliations/${editingId}`, { ...form, edited_by: currentUser });
      } else {
        await axios.post(`${API}/compliance/outside-affiliations`, { ...form, submitted_by: currentUser });
      }
      setShowForm(false); setEditingId(null); setForm(blank); load();
      // DECLMSGI18N: the SAME message as the PAD success toast, so it reads from the same key
      // rather than staying hardcoded English beside a translated twin.
      showToast(t('compliance.padSaved'), 'success');
    } catch (e) {
      setError(fmtApiError(e));
      showToast(fmtApiError(e), 'error');
    }
    setSaving(false);
  };

  const startEdit = (rec) => {
    setForm({
      affiliated_entity: rec.affiliated_entity || '',
      nature_activities: rec.nature_activities || '',
      position: rec.position || '',
      position_details: rec.position_details || '',
      date_commenced: rec.date_commenced || '',
      additional_information: rec.additional_information || '',
      is_competitor: rec.is_competitor || false,
      is_paid_employment: rec.is_paid_employment || false,
      is_none_of_above: rec.is_none_of_above || false,
      status: rec.status || 'Pending',
    });
    setEditingId(rec.id); setShowForm(true);
  };

  // NAMERES: delegates to the shared resolver. Kept as a thin local alias so the two call
  // sites below read unchanged, and so this tab cannot drift from the other three.
  // ROWNAME: `row` optional, so both of this tab's call sites can hand over the
  // record and get the server's name first.
  const empName = (id, row) => resolveEmployeeName(employees, id, t, row);

  return (
    <div>
      <div style={{ backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: 8, padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
        <p style={{ margin: '0 0 8px 0' }}>{t('compliance.oa.policy')}</p>
        <PolicyLink category="Conflicts of Interest" />
      </div>

      <div style={s.tabBar}>
        {isAdmin && (
          <select style={s.filterSelect} value={filterEmp}
            onChange={e => setFilterEmp(e.target.value)}>
            <option value="">{t('common.allEmployees')}</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        )}
        {!readOnly && !canChangeStatus && (
          <button style={s.newBtn} onClick={() => { setShowForm(true); setEditingId(null); setForm(blank); }}>
            {t('compliance.newDeclaration')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={s.formBox}>
          <div style={s.formTitleRow}>
            <h3 style={s.formTitle}>{editingId ? t('compliance.editDeclaration') : t('compliance.oa.formTitle')}</h3>
            {!editingId && isTestAccount() && (
              <button style={s.demoBtn} onClick={() => setForm({
                affiliated_entity: 'Acacia Capital Partners',
                nature_activities: 'Private equity fund management and advisory services.',
                position: 'Director',
                position_details: '',
                date_commenced: todayISO(),
                additional_information: "No overlap with Enko's current portfolio.",
                is_competitor: false,
                is_paid_employment: false,
                is_none_of_above: true,
              })}>{t('compliance.fillDemo')}</button>
            )}
          </div>
          {error && <div style={s.error} role="alert" aria-live="polite">{error}</div>}

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f`}>{t('compliance.oa.affiliatedEntity')}</label>
              
              <input id={`${a11yId}-f`} style={s.input} value={form.affiliated_entity}
                onChange={e => setForm({ ...form, affiliated_entity: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-2`}>{t('compliance.oa.dateCommenced')}</label>
              
              <input id={`${a11yId}-f-2`} style={s.input} type="date" value={form.date_commenced}
                onChange={e => setForm(f => ({ ...f, date_commenced: e.target.value }))} />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-3`}>{t('compliance.oa.activitiesNature')}</label>
            
            <textarea id={`${a11yId}-f-3`} style={s.textarea} rows={2} value={form.nature_activities}
              onChange={e => setForm({ ...form, nature_activities: e.target.value })} />
          </div>

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-4`}>{t('compliance.oa.position')}</label>
              
              <select id={`${a11yId}-f-4`} style={s.input} value={form.position}
                onChange={e => setForm({ ...form, position: e.target.value, position_details: '' })}>
                <option value=""></option>
                {POSITION_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {(form.position === 'Other' || form.position === 'Governmental official') && (
              <div style={s.field}>
                <label style={s.label} htmlFor={`${a11yId}-f-5`}>{t('compliance.oa.positionDetails')}</label>
                
                <input id={`${a11yId}-f-5`} style={s.input} value={form.position_details}
                  onChange={e => setForm({ ...form, position_details: e.target.value })} />
              </div>
            )}
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-6`}>{t('compliance.oa.additionalInfo')}</label>
            
            <textarea id={`${a11yId}-f-6`} style={s.textarea} rows={2} value={form.additional_information}
              onChange={e => setForm({ ...form, additional_information: e.target.value })} />
          </div>

          <div style={{ ...s.field, marginTop: 4 }}>
            <label style={{ ...s.label, marginBottom: 10 }}>{t('compliance.oa.checkboxLabel')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#333', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_competitor}
                  onChange={e => setForm({ ...form, is_competitor: e.target.checked, is_none_of_above: false })}
                  style={{ marginTop: 2, accentColor: '#1d4ed8' }} />
                {t('compliance.oa.competitorFull')}
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#333', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_paid_employment}
                  onChange={e => setForm({ ...form, is_paid_employment: e.target.checked, is_none_of_above: false })}
                  style={{ marginTop: 2, accentColor: '#1d4ed8' }} />
                {t('compliance.oa.paidEmploymentFull')}
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#333', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_none_of_above}
                  onChange={e => setForm({ ...form, is_none_of_above: e.target.checked, is_competitor: false, is_paid_employment: false })}
                  style={{ marginTop: 2, accentColor: '#1d4ed8' }} />
                {t('compliance.oa.noneOfAbove')}
              </label>
            </div>
          </div>

          {canChangeStatus && editingId && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-7`}>{t('compliance.statusLabel')}</label>
              
              <select id={`${a11yId}-f-7`} style={s.input} value={form.status || 'Pending'}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Pending">{t('compliance.status.pending')}</option>
                <option value="Approved">{t('compliance.status.approved')}</option>
                <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
                <option value="Ceased">{t('compliance.status.ceased')}</option>
              </select>
            </div>
          )}

          <div style={s.btnRow}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setEditingId(null); }}>{t('compliance.cancel')}</button>
            <button style={s.submitBtn} onClick={submit} disabled={saving}>
              {saving ? <><Spinner /> {t('compliance.saving')}</> : (editingId ? t('compliance.saveChanges') : t('common.submit'))}
            </button>
          </div>
        </div>
      )}

      {/* Cease date picker inline */}
      {ceaseId && (
        <div style={{ ...s.formBox, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={s.label} htmlFor={`${a11yId}-f-8`}>{t('compliance.cessationDate')} *</label>
          
          <input id={`${a11yId}-f-8`} style={{ ...s.input, width: 180 }} type="date" value={ceaseDate}
            onChange={e => setCeaseDate(e.target.value)} />
          <button style={s.submitBtn} onClick={handleCease} disabled={!ceaseDate}>{t('compliance.confirm')}</button>
          <button style={s.cancelBtn} onClick={() => { setCeaseId(null); setCeaseDate(''); }}>{t('compliance.cancel')}</button>
        </div>
      )}

      {loading ? <p style={s.empty}>{t('compliance.loading')}</p> : loadFailed ? (
        <LoadError message="Couldn't load these declarations." onRetry={load} />
      ) : records.length === 0 ? (
        <p style={s.empty}>{t('compliance.noDeclarations')}</p>
      ) : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              {isAdmin && <th style={s.th}>{t('compliance.tableEmployee')}</th>}
              <th style={s.th}>{t('compliance.oa.tableEntity')}</th>
              <th style={s.th}>{t('compliance.oa.tablePosition')}</th>
              <th style={s.th}>{t('compliance.oa.tableDateCommenced')}</th>
              <th style={s.th}>{t('compliance.oa.tableDateCeased')}</th>
              {/* DECLDELAY: the submission date and the reporting delay, on the screen the
                  officer works in daily. Placed immediately BEFORE Status so the two read together
                  at the right-hand end of the row, after the declaration's own facts. Both stay
                  visible on a narrow screen: the table already scrolls horizontally in its own
                  container, so no column is dropped or collapsed at any width. */}
              <th style={s.th}>{t('compliance.tableSubmittedOn')}</th>
              <th style={{ ...s.th, whiteSpace: 'nowrap' }}>{t('compliance.tableDaysToReport')}</th>
              <th style={s.th}>{t('common.status')}</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {records.map(rec => (
              <React.Fragment key={rec.id}>
                <tr style={s.tr}>
                  {isAdmin && (
                    <td style={s.td}>
                      {empName(rec.employee_id, rec)}
                      {/* DECLENTITY: see EntityLine. */}
                      <EntityLine entity={rec.entity} />
                    </td>
                  )}
                  <td style={s.td}>
                    <span style={{ fontWeight: 500 }}>{rec.affiliated_entity || '-'}</span>
                    {rec.nature_activities && <p style={{ fontSize: 11, color: '#888', margin: '2px 0 0 0' }}>{rec.nature_activities}</p>}
                  </td>
                  <td style={s.td}>
                    {rec.position || '-'}
                    {rec.position_details && <p style={{ fontSize: 11, color: '#888', margin: '2px 0 0 0' }}>{rec.position_details}</p>}
                  </td>
                  <td style={s.td}>{fmt(rec.date_commenced)}</td>
                  <td style={s.td}>{rec.date_ceased ? fmt(rec.date_ceased) : <span style={{ color: '#aaa' }}>{t('compliance.active')}</span>}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmt(rec.created_at)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtDelay(rec.days_to_report)}</td>
                  <td style={s.td}>{statusBadge(rec.status, t)}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {/* COMPMODAL: reviewer opens the full submitted details + ReviewPanel in a modal. */}
                    {canChangeStatus && (
                      <button style={sM.viewBtn} onClick={() => setDetailsRec(rec)}>{t('compliance.view', 'View')}</button>
                    )}
                    {!readOnly && !canChangeStatus && rec.status === 'Pending' && (
                      <button style={{ ...s.editBtn, marginLeft: 4 }} onClick={() => startEdit(rec)}>{t('compliance.amend')}</button>
                    )}
                    {/* CEASEUIONLY (confirmed COO decision, finishing the CEASEREVERSE line):
                        the Cease button is now OWNER-ONLY in the UI. The declaration OWNER may
                        report cessation of their OWN outside affiliation while it is still Pending
                        (same owner scoping the Amend button above uses: !readOnly &&
                        !canChangeStatus && Pending). Compliance/coo/admin no longer see a Cease
                        button at all - the previous canChangeStatus button was removed. Backend
                        permission is UNCHANGED: compliance/coo/admin can still cease via direct API
                        as an override, and the admin status <select> below still offers "Ceased". */}
                    {!readOnly && !canChangeStatus && rec.status === 'Pending' && !rec.date_ceased && (
                      <button style={{ ...s.editBtn, marginLeft: 4, color: '#c0392b', borderColor: '#e74c3c' }}
                        onClick={() => { setCeaseId(rec.id); setCeaseDate(todayISO()); }}>
                        {t('compliance.cease')}
                      </button>
                    )}
                    {canChangeStatus && isAdmin && (
                      <select value={rec.status || 'Pending'}
                        onChange={e => handleStatusChange(rec, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginLeft: 4 }}>
                        <option value="Pending">{t('compliance.status.pending')}</option>
                        <option value="Approved">{t('compliance.status.approved')}</option>
                        <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
                        <option value="Ceased">{t('compliance.status.ceased')}</option>
                      </select>
                    )}
                  </td>
                </tr>
                {rec.edit_history && rec.edit_history !== '[]' && (
                  <tr><td colSpan={isAdmin ? 9 : 8}><EditLog history={rec.edit_history} names={rec.edited_by_names} t={t} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table></ScrollableTable>
      )}
      {/* COMPMODAL: single modal per tab, driven by detailsRec. Details + ReviewPanel together. */}
      {canChangeStatus && (
        <DeclarationDetailsModal
          rec={detailsRec}
          title={detailsRec ? (detailsRec.affiliated_entity || 'Outside Affiliation') : ''}
          subtitle={detailsRec ? empName(detailsRec.employee_id, detailsRec) : ''}
          rows={detailsRec ? [
            ['Affiliated entity', detailsRec.affiliated_entity],
            ['Nature of activities', detailsRec.nature_activities],
            ['Position', detailsRec.position],
            ['Position details', detailsRec.position_details],
            ['Date commenced', fmt(detailsRec.date_commenced)],
            ['Date ceased', detailsRec.date_ceased ? fmt(detailsRec.date_ceased) : null],
            ['Is a competitor of Enko', _yesNo(detailsRec.is_competitor)],
            ['Is paid employment', _yesNo(detailsRec.is_paid_employment)],
            ['None of the above', _yesNo(detailsRec.is_none_of_above)],
            ['Additional information', detailsRec.additional_information],
            ['Submitted by', submitterName(detailsRec)],
          ] : []}
          onClose={() => setDetailsRec(null)}
        >
          {detailsRec && (
            <ReviewPanel declType="outside-affiliations" rec={detailsRec}
              onReviewed={() => { setDetailsRec(null); load(); }} />
          )}
        </DeclarationDetailsModal>
      )}
    </div>
  );
}

// ─── Tab: Gifts & Entertainment ──────────────────────────

const NATURE_OPTIONS = ['Lunch/Dinner meeting', 'Gift', 'Attendance at an Event'];
// GEHBENEFIT: the one relationship for which "Benefit to underlying fund/investors" is
// mandatory. Declared once and compared against, rather than repeating the string at each use,
// and it matches GEH_BENEFIT_REQUIRED_RELATIONSHIP on the server.
const BENEFIT_REQUIRED_RELATIONSHIP = 'Trading Counterparty';

const RELATIONSHIP_OPTIONS = [
  'Trading Counterparty',
  'Investor or Prospective Investor',
  'Service Provider',
  'Government Official',
  'Other',
];
const THRESHOLD_OPTIONS = [
  'Below the threshold applicable to me within the Anti-Bribery and Corruption Policy',
  'Above the threshold applicable to me within the Anti-Bribery and Corruption Policy, and this notification serves to seek approval from compliance',
  'Above the threshold applicable to me within the Anti-Bribery and Corruption Policy, and approval from compliance has been obtained separately',
];

function GiftsTab({ entityScope, currentUser, employeeId, isAdmin, employees, readOnly, canChangeStatus }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const showToast = useToast();
  // RESPONSIVE Phase 2: two-column field rows reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...s.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [detailsRec, setDetailsRec] = useState(null);  // COMPMODAL

  const blank = {
    entry_type: '', date: '', nature: '', purpose: '',
    firm_name: '', individual_contact: '',
    firm_relationship: '', firm_relationship_other: '',
    currency: 'GBP', amount: '', threshold_status: '',
    // GEHBENEFIT: mandatory only when the relationship is a trading counterparty.
    benefit_to_fund: '',
  };
  const [form, setForm] = useState(blank);

  // LOADERR: distinguishes a FAILED register load from a genuinely empty one.
  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const params = isAdmin
      ? (filterEmp ? { employee_id: filterEmp } : {})
      : { employee_id: employeeId };
    axios.get(`${API}/compliance/gifts`, { params })
      .then(res => {
        // COMPSCOPEUI: show only rows this account may actually reach. `employees` is the
        // already-scoped list, so a record whose employee is not in it is out of scope and
        // the backend would refuse it anyway. Unscoped (entityScope null) keeps every row,
        // so coo, admin and the CCO see exactly what they saw before.
        const rows = res.data || [];
        const allowed = new Set((employees || []).map(e => e.id));
        setRecords(entityScope ? rows.filter(r => allowed.has(r.employee_id)) : rows);
        setLoadFailed(false);
      })
      // LOADERR: a failed load must not render as "no entries".
      .catch(() => { setRecords([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [isAdmin, employeeId, filterEmp, entityScope, employees]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (rec, newStatus) => {
    try {
      await axios.put(`${API}/compliance/gifts/${rec.id}`, { status: newStatus, edited_by: currentUser });
      load();
    } catch (e) { showToast(fmtApiError(e), 'error'); }
  };

  const submit = async () => {
    if (!form.entry_type || !form.date || !form.nature || !form.purpose ||
        !form.firm_name || !form.firm_relationship || !form.currency ||
        !form.amount || !form.threshold_status) {
      // ERRREQI18N: the locale key, which PREAPPROVALCELL left orphaned when it stopped misusing
      // it as a table-cell fallback. This is its real job: the form's own validation message.
      setError(t('compliance.errRequired')); return;
    }
    if (form.firm_relationship === 'Other' && !form.firm_relationship_other) {
      setError('Please specify the firm relationship.'); return;
    }
    // GEHBENEFIT: the same conditional shape the relationship-other check above already uses.
    // The SERVER enforces this too (_validate_gift_benefit), so bypassing this check does not
    // get the declaration stored; this exists to say so sooner and in better words.
    if (form.firm_relationship === BENEFIT_REQUIRED_RELATIONSHIP
        && !(form.benefit_to_fund || '').trim()) {
      setError('Please state the benefit to the underlying fund or its investors.'); return;
    }
    setSaving(true); setError(null);
    try {
      const payload = { ...form, date: toISODate(form.date) };
      if (editingId) {
        await axios.put(`${API}/compliance/gifts/${editingId}`, { ...payload, edited_by: currentUser });
      } else {
        await axios.post(`${API}/compliance/gifts`, { ...payload, submitted_by: currentUser });
      }
      setShowForm(false); setEditingId(null); setForm(blank); load();
      // DECLMSGI18N: the SAME message as the PAD success toast, so it reads from the same key
      // rather than staying hardcoded English beside a translated twin.
      showToast(t('compliance.padSaved'), 'success');
    } catch (e) {
      setError(fmtApiError(e));
      showToast(fmtApiError(e), 'error');
    }
    setSaving(false);
  };

  const startEdit = (rec) => {
    setForm({
      entry_type: rec.entry_type || '', date: rec.date || '',
      nature: rec.nature || '', purpose: rec.purpose || '',
      firm_name: rec.firm_name || '', individual_contact: rec.individual_contact || '',
      // GEHBENEFIT: an older record has no value, which loads as an empty field rather than as
      // an error. Nobody is asked to complete a past declaration.
      benefit_to_fund: rec.benefit_to_fund || '',
      firm_relationship: rec.firm_relationship || '',
      firm_relationship_other: rec.firm_relationship_other || '',
      currency: rec.currency || 'GBP', amount: rec.amount || '',
      threshold_status: rec.threshold_status || '',
      status: rec.status || 'Pending',
    });
    setEditingId(rec.id); setShowForm(true);
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: '8px', padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
        <p style={{ margin: '0 0 8px 0' }}>{t('compliance.geh.policy')}</p>
        <PolicyLink category="Anti-Bribery and Corruption" />
      </div>
      <div style={s.tabBar}>
        {isAdmin && (
          <select style={s.filterSelect} value={filterEmp}
            onChange={e => setFilterEmp(e.target.value)}>
            <option value="">{t('common.allEmployees')}</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        )}
        {!readOnly && !canChangeStatus && (
          <button style={s.newBtn} onClick={() => { setShowForm(true); setEditingId(null); setForm(blank); }}>
            {t('compliance.newEntry')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={s.formBox}>
          <div style={s.formTitleRow}>
            <h3 style={s.formTitle}>{editingId ? t('compliance.editEntry') : t('compliance.geh.formTitle')}</h3>
            {!editingId && isTestAccount() && (
              <button style={s.demoBtn} onClick={() => setForm({
                entry_type: 'Receive',
                date: todayISO(),
                nature: 'Lunch/Dinner meeting',
                purpose: 'Quarterly portfolio review',
                firm_name: 'Counterparty Bank plc',
                individual_contact: 'Relationship manager',
                firm_relationship: 'Trading Counterparty',
                firm_relationship_other: '',
                currency: 'GBP',
                amount: '120',
                threshold_status: THRESHOLD_OPTIONS[0],
                // GEHBENEFIT: this demo selects a trading counterparty, so it must carry the
                // benefit statement, otherwise the demo would fail its own validation.
                benefit_to_fund: 'Research access supporting best execution for the fund',
              })}>{t('compliance.fillDemo')}</button>
            )}
          </div>

          {error && <div style={s.error} role="alert" aria-live="polite">{error}</div>}

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-9`}>{t('compliance.geh.type')}</label>
              
              <select id={`${a11yId}-f-9`} style={s.input} value={form.entry_type}
                onChange={e => setForm({ ...form, entry_type: e.target.value })}>
                <option value=""></option>
                <option value="Receive">{t('compliance.geh.receive')}</option>
                <option value="Offer">{t('compliance.geh.offer')}</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-10`}>{t('compliance.geh.date')}</label>
              
              <input id={`${a11yId}-f-10`} style={s.input} type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-11`}>{t('compliance.geh.nature')}</label>
              
              <select id={`${a11yId}-f-11`} style={s.input} value={form.nature}
                onChange={e => setForm({ ...form, nature: e.target.value })}>
                <option value=""></option>
                {NATURE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-12`}>{t('compliance.geh.purpose')}</label>
              
              <input id={`${a11yId}-f-12`} style={s.input} value={form.purpose}
                onChange={e => setForm({ ...form, purpose: e.target.value })} />
            </div>
            <div style={s.field}>
              {/* GEHBENEFIT: the required marker appears only for the relationship that
                  requires it, so the label never claims to be mandatory when it is not. */}
              <label style={s.label} htmlFor={`${a11yId}-f-13`}>
                {t('compliance.geh.benefitToFund')}
                {form.firm_relationship === BENEFIT_REQUIRED_RELATIONSHIP ? ' *' : ''}
              </label>
              
              <input id={`${a11yId}-f-13`} style={s.input} value={form.benefit_to_fund}
                placeholder={t('compliance.geh.benefitToFundPlaceholder')}
                onChange={e => setForm({ ...form, benefit_to_fund: e.target.value })} />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-14`}>{t('compliance.geh.firmName')}</label>
              
              <input id={`${a11yId}-f-14`} style={s.input} value={form.firm_name}
                onChange={e => setForm({ ...form, firm_name: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-15`}>{t('compliance.geh.individualContact')}</label>
              
              <input id={`${a11yId}-f-15`} style={s.input} value={form.individual_contact}
                onChange={e => setForm({ ...form, individual_contact: e.target.value })} />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-16`}>{t('compliance.geh.firmRelationship')}</label>
            
            <select id={`${a11yId}-f-16`} style={s.input} value={form.firm_relationship}
              onChange={e => setForm({ ...form, firm_relationship: e.target.value, firm_relationship_other: '' })}>
              <option value=""></option>
              {RELATIONSHIP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          {form.firm_relationship === 'Other' && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-17`}>{t('compliance.geh.firmRelationshipOther')}</label>
              
              <input id={`${a11yId}-f-17`} style={s.input} value={form.firm_relationship_other}
                onChange={e => setForm({ ...form, firm_relationship_other: e.target.value })} />
            </div>
          )}

          <div style={s.field}>
            <label style={s.label}>{t('compliance.geh.amount')}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select style={{ ...s.input, width: '110px', flexShrink: 0 }} value={form.currency}
                onChange={e => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input style={s.input} type="number" min="0" step="0.01" value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })} />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>{t('compliance.geh.thresholdStatus')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
              {THRESHOLD_OPTIONS.map((opt, i) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#333', lineHeight: '1.5' }}>
                  <input type="radio" name="threshold_status" value={opt}
                    checked={form.threshold_status === opt}
                    onChange={() => setForm({ ...form, threshold_status: opt })}
                    style={{ marginTop: '3px', flexShrink: 0 }} />
                  {t(`compliance.geh.threshold${i + 1}`)}
                </label>
              ))}
            </div>
          </div>

          {canChangeStatus && editingId && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-18`}>{t('compliance.statusLabel')}</label>
              
              <select id={`${a11yId}-f-18`} style={s.input} value={form.status || 'Pending'}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Pending">{t('compliance.status.pending')}</option>
                <option value="Approved">{t('compliance.status.approved')}</option>
                <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
              </select>
            </div>
          )}

          <div style={s.btnRow}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setEditingId(null); }}>{t('compliance.cancel')}</button>
            <button style={s.submitBtn} onClick={submit} disabled={saving}>
              {saving ? <><Spinner /> {t('compliance.saving')}</> : (editingId ? t('compliance.saveChanges') : t('common.submit'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <p style={s.empty}>{t('compliance.loading')}</p> : loadFailed ? (
        <LoadError message="Couldn't load these entries." onRetry={load} />
      ) : records.length === 0 ? (
        <p style={s.empty}>{t('compliance.noEntries')}</p>
      ) : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              {isAdmin && <th style={s.th}>{t('compliance.tableEmployee')}</th>}
              <th style={s.th}>{t('compliance.geh.tableType')}</th>
              <th style={s.th}>{t('compliance.geh.tableDate')}</th>
              <th style={s.th}>{t('compliance.geh.tableNature')}</th>
              <th style={s.th}>{t('compliance.geh.tableFirm')}</th>
              <th style={s.th}>{t('compliance.geh.tableRelationship')}</th>
              <th style={s.th}>{t('compliance.geh.tableValue')}</th>
              <th style={s.th}>{t('compliance.geh.tableThreshold')}</th>
              {/* DECLDELAY: the submission date and the reporting delay, on the screen the
                  officer works in daily. Placed immediately BEFORE Status so the two read together
                  at the right-hand end of the row, after the declaration's own facts. Both stay
                  visible on a narrow screen: the table already scrolls horizontally in its own
                  container, so no column is dropped or collapsed at any width. */}
              <th style={s.th}>{t('compliance.tableSubmittedOn')}</th>
              <th style={{ ...s.th, whiteSpace: 'nowrap' }}>{t('compliance.tableDaysToReport')}</th>
              <th style={s.th}>{t('compliance.geh.tableStatus')}</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {records.map(rec => (
              <React.Fragment key={rec.id}>
                <tr style={s.tr}>
                  {isAdmin && (
                    <td style={s.td}>
                      {/* NAMERES: the shared resolver, never a raw id. */}
                      {resolveEmployeeName(employees, rec.employee_id, t, rec)}
                      {/* DECLENTITY: the canonical entity beneath, so two same-name employees
                          are distinguishable. Server-resolved; absent renders nothing. */}
                      <EntityLine entity={rec.entity} />
                    </td>
                  )}
                  <td style={s.td}>
                    <span style={{ ...s.badge, backgroundColor: rec.entry_type === 'Offer' ? '#e8f0ff' : '#fff0e8', color: rec.entry_type === 'Offer' ? '#1a3a8e' : '#8e3a1a' }}>
                      {rec.entry_type || '-'}
                    </span>
                  </td>
                  <td style={s.td}>{fmt(rec.date)}</td>
                  <td style={s.td}>{rec.nature || '-'}</td>
                  <td style={s.td}>
                    <span style={{ fontWeight: 500 }}>{rec.firm_name || '-'}</span>
                    {rec.individual_contact && <span style={s.meta}><br />{rec.individual_contact}</span>}
                  </td>
                  <td style={s.td}>
                    {rec.firm_relationship || '-'}
                    {rec.firm_relationship === 'Other' && rec.firm_relationship_other && (
                      <span style={s.meta}><br />{rec.firm_relationship_other}</span>
                    )}
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{rec.currency} {rec.amount || '-'}</td>
                  <td style={{ ...s.td, maxWidth: '200px', fontSize: '12px', color: '#555' }}>
                    {rec.threshold_status || '-'}
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmt(rec.created_at)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtDelay(rec.days_to_report)}</td>
                  <td style={s.td}>{statusBadge(rec.status, t)}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {canChangeStatus && (
                      <button style={sM.viewBtn} onClick={() => setDetailsRec(rec)}>{t('compliance.view', 'View')}</button>
                    )}
                    {!readOnly && !canChangeStatus && rec.status === 'Pending' && (
                      <button style={{ ...s.editBtn, marginLeft: 4 }} onClick={() => startEdit(rec)}>{t('compliance.amend')}</button>
                    )}
                    {canChangeStatus && (
                      <select value={rec.status || 'Pending'}
                        onChange={e => handleStatusChange(rec, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginLeft: 4 }}>
                        <option value="Pending">{t('compliance.status.pending')}</option>
                        <option value="Approved">{t('compliance.status.approved')}</option>
                        <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
                      </select>
                    )}
                  </td>
                </tr>
                {rec.edit_history && rec.edit_history !== '[]' && (
                  <tr><td colSpan={isAdmin ? 12 : 11}><EditLog history={rec.edit_history} names={rec.edited_by_names} t={t} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table></ScrollableTable>
      )}
      {canChangeStatus && (
        <DeclarationDetailsModal
          rec={detailsRec}
          title={detailsRec ? (detailsRec.firm_name || 'Gift / Entertainment') : ''}
          subtitle={detailsRec ? resolveEmployeeName(employees, detailsRec.employee_id, t, detailsRec) : ''}
          rows={detailsRec ? [
            ['Type', detailsRec.entry_type],
            ['Date', fmt(detailsRec.date)],
            ['Nature', detailsRec.nature],
            // GEHLABEL: the LABEL only. The field is still `purpose` and no data changed.
            ['Detail of GE&H', detailsRec.purpose],
            ['Benefit to underlying fund/investors', detailsRec.benefit_to_fund],
            ['Firm', detailsRec.firm_name],
            ['Individual contact', detailsRec.individual_contact],
            ['Firm relationship', detailsRec.firm_relationship],
            ['Relationship (other)', detailsRec.firm_relationship_other],
            ['Amount', (detailsRec.currency || '') + ' ' + (detailsRec.amount || '')],
            ['Threshold status', detailsRec.threshold_status],
            ['Submitted by', submitterName(detailsRec)],
          ] : []}
          onClose={() => setDetailsRec(null)}
        >
          {detailsRec && (
            <ReviewPanel declType="gifts" rec={detailsRec}
              onReviewed={() => { setDetailsRec(null); load(); }} />
          )}
        </DeclarationDetailsModal>
      )}
    </div>
  );
}

// ─── Tab: Personal Account Dealing ───────────────────────

const PAD_PRE_APPROVAL_OPTIONS = [
  'Does not require pre-approval under the Policy',
  'Requires pre-approval as there is overlap between the personal trade and instruments that Enko may invest in, as described in the Policy',
  'Requires pre-approval as the personal trade is a SELL being carried out ahead of the mandatory holding period',
];
const UNLISTED_ATTESTATION_TEXT =
  'I confirm that the investment is not made in respect of any entity that to my knowledge formed part of a deal pipeline for an Enko fund within the previous 2 years';

function PADTab({ entityScope, currentUser, employeeId, isAdmin, employees, readOnly, canChangeStatus }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const showToast = useToast();
  // RESPONSIVE Phase 2: two-column field rows reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...s.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSecurity, setFilterSecurity] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmFile, setConfirmFile] = useState(null);
  const [preApprovalFile, setPreApprovalFile] = useState(null);
  const [detailsRec, setDetailsRec] = useState(null);  // COMPMODAL

  const blank = {
    trade_for: 'Self', close_link_details: '',
    trade_date: '', trade_type: '',
    security: '', direction: '', currency: 'GBP',
    price: '', quantity: '',
    pre_approval_type: '', unlisted_attestation: false,
  };
  const [form, setForm] = useState(blank);

  const needsPreApproval = form.pre_approval_type && form.pre_approval_type !== PAD_PRE_APPROVAL_OPTIONS[0];

  // LOADERR: distinguishes a FAILED register load from a genuinely empty one.
  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const params = isAdmin
      ? (filterEmp ? { employee_id: filterEmp } : {})
      : { employee_id: employeeId };
    axios.get(`${API}/compliance/pad`, { params })
      .then(res => {
        // COMPSCOPEUI: show only rows this account may actually reach. `employees` is the
        // already-scoped list, so a record whose employee is not in it is out of scope and
        // the backend would refuse it anyway. Unscoped (entityScope null) keeps every row,
        // so coo, admin and the CCO see exactly what they saw before.
        const rows = res.data || [];
        const allowed = new Set((employees || []).map(e => e.id));
        setRecords(entityScope ? rows.filter(r => allowed.has(r.employee_id)) : rows);
        setLoadFailed(false);
      })
      // LOADERR: a failed load must not render as "no trades".
      .catch(() => { setRecords([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [isAdmin, employeeId, filterEmp, entityScope, employees]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (rec, newStatus) => {
    try {
      await axios.put(`${API}/compliance/pad/${rec.id}`, { status: newStatus, edited_by: currentUser });
      load();
    } catch (e) { showToast(fmtApiError(e), 'error'); }
  };

  const submit = async () => {
    if (!form.trade_for || !form.trade_date || !form.trade_type || !form.security ||
        !form.direction || !form.currency || !form.price || !form.quantity || !form.pre_approval_type) {
      // ERRREQI18N: the locale key, which PREAPPROVALCELL left orphaned when it stopped misusing
      // it as a table-cell fallback. This is its real job: the form's own validation message.
      setError(t('compliance.errRequired')); return;
    }
    if (form.trade_for === 'Close link' && !form.close_link_details) {
      setError('Please provide close link details.'); return;
    }
    if (!confirmFile && !editingId) { setError('Broker trade confirmation is required.'); return; }
    if (needsPreApproval && !preApprovalFile && !editingId) {
      setError('Pre-approval document is required for this pre-approval type.'); return;
    }
    if (form.trade_type === 'Unlisted / private investment' && !form.unlisted_attestation) {
      setError('Please confirm the unlisted investment attestation.'); return;
    }
    setSaving(true); setError(null);
    try {
      let rec;
      const payload = { ...form, trade_date: toISODate(form.trade_date) };
      if (editingId) {
        const res = await axios.put(`${API}/compliance/pad/${editingId}`, { ...payload, edited_by: currentUser });
        rec = res.data;
      } else {
        const res = await axios.post(`${API}/compliance/pad`, { ...payload, submitted_by: currentUser });
        rec = res.data;
      }
      // PADCOMMIT: THE DECLARATION IS SAVED AT THIS POINT. Everything below is a follow-up step,
      // and a follow-up failure must never be reported as a failed submission.
      //
      // THE DEFECT THIS REMOVES: both uploads sat inside the same try as the POST, so a failed
      // attachment upload landed in the catch below and the employee was told their submission had
      // failed. It had not. That is the same mistake the backend made after its own commit, and it
      // produces the same outcome: the employee submits the whole declaration again.
      //
      // Each upload is awaited separately and its failure recorded, so one failing does not skip
      // the other and neither turns the saved declaration into an error.
      const outstanding = [];
      if (confirmFile && rec?.id) {
        try {
          const fd = new FormData();
          fd.append('file', confirmFile);
          await axios.post(`${API}/compliance/pad/${rec.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          // UPLOADREFUSE: name the file AND the server's reason.
          outstanding.push(`the broker trade confirmation (${refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed')})`);
        }
      }
      if (preApprovalFile && rec?.id) {
        try {
          const fd = new FormData();
          fd.append('file', preApprovalFile);
          await axios.post(`${API}/compliance/pad/${rec.id}/upload-preapproval`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          outstanding.push(`the pre-approval document (${refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed')})`);
        }
      }

      setShowForm(false); setEditingId(null); setForm(blank); setConfirmFile(null); setPreApprovalFile(null); load();

      // PADCOMMIT: say plainly that the declaration is saved, and name what still has to be done.
      // notification_sent is false when the declaration saved but compliance could not be emailed.
      // DECLMSGI18N: the three PAD submission messages now come from the locale files, in English and
      // French, instead of being hardcoded English strings. They were the last untranslated messages
      // on this screen, so a French-reading officer got English at the one moment that matters most:
      // the confirmation that her declaration did or did not save.
      //
      // The WORDING is unchanged in English, deliberately: PADCOMMIT chose each sentence to say
      // plainly that the record IS saved and name what still has to be done, and this moves the text
      // without re-deciding it. The French carries the same two facts.
      if (outstanding.length) {
        showToast(
          t('compliance.padSavedUploadFailed', { what: outstanding.join(' and ') }),
          'error',
        );
      } else if (rec && rec.notification_sent === false) {
        showToast(t('compliance.padSavedNotNotified'), 'error');
      } else {
        showToast(t('compliance.padSaved'), 'success');
      }
    } catch (e) {
      // Reached only when the declaration itself was refused, so nothing was saved and retrying is
      // the right advice. A 409 from the duplicate guard arrives here with the server's own
      // sentence, which already explains that the earlier submission did save.
      setError(fmtApiError(e));
      showToast(fmtApiError(e), 'error');
    }
    setSaving(false);
  };

  const startEdit = (rec) => {
    setForm({
      trade_for: rec.trade_for || 'Self',
      close_link_details: rec.close_link_details || '',
      trade_date: rec.trade_date || '',
      trade_type: rec.trade_type || '',
      security: rec.security || '',
      direction: rec.direction || '',
      currency: rec.currency || 'GBP',
      price: rec.price || '',
      quantity: rec.quantity || '',
      pre_approval_type: rec.pre_approval_type || '',
      unlisted_attestation: rec.unlisted_attestation || false,
      status: rec.status || 'Pending',
    });
    setEditingId(rec.id); setShowForm(true);
  };

  const filtered = records.filter(r => {
    if (filterSecurity && !r.security?.toLowerCase().includes(filterSecurity.toLowerCase())) return false;
    if (filterDateFrom && r.trade_date < filterDateFrom) return false;
    if (filterDateTo && r.trade_date > filterDateTo) return false;
    return true;
  });

  return (
    <div>
      <div style={{ backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: '8px', padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
        <p style={{ margin: '0 0 8px 0' }}>{t('compliance.pad.policy')}</p>
        <PolicyLink category="Personal Account Dealing" />
      </div>
      <div style={s.tabBar}>
        {isAdmin && (
          <select style={s.filterSelect} value={filterEmp}
            onChange={e => setFilterEmp(e.target.value)}>
            <option value="">{t('common.allEmployees')}</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        )}
        <input style={s.filterInput} type="text" placeholder={t('compliance.pad.tableSecurity') + '…'}
          value={filterSecurity} onChange={e => setFilterSecurity(e.target.value)} />
        <input style={s.filterInput} type="date" title={t('compliance.pad.tradeDate')}
          value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
        <input style={s.filterInput} type="date" title={t('compliance.pad.tradeDate')}
          value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
        {!readOnly && !canChangeStatus && (
          <button style={s.newBtn} onClick={() => { setShowForm(true); setEditingId(null); setForm(blank); setConfirmFile(null); setPreApprovalFile(null); }}>
            {t('compliance.newTrade')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={s.formBox}>
          <div style={s.formTitleRow}>
            <h3 style={s.formTitle}>{editingId ? t('compliance.editTrade') : t('compliance.pad.formTitle')}</h3>
            {!editingId && isTestAccount() && (
              <button style={s.demoBtn} onClick={() => setForm({
                trade_for: 'Self', close_link_details: '',
                trade_date: todayISO(),
                trade_type: 'Listed market',
                security: 'AAPL US Equity',
                direction: 'Buy', currency: 'USD',
                price: '189.50', quantity: '50',
                pre_approval_type: PAD_PRE_APPROVAL_OPTIONS[0],
                unlisted_attestation: false,
              })}>{t('compliance.fillDemo')}</button>
            )}
          </div>

          {error && <div style={s.error} role="alert" aria-live="polite">{error}</div>}

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-19`}>{t('compliance.pad.tradeFor')}</label>
              
              <select id={`${a11yId}-f-19`} style={s.input} value={form.trade_for}
                onChange={e => setForm({ ...form, trade_for: e.target.value, close_link_details: '' })}>
                <option value="Self">{t('compliance.pad.self')}</option>
                <option value="Spouse or household member">{t('compliance.pad.spouse')}</option>
                <option value="Close link">{t('compliance.pad.closeLink')}</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-20`}>{t('compliance.pad.tradeDate')}</label>
              
              <input id={`${a11yId}-f-20`} style={s.input} type="date" value={form.trade_date}
                onChange={e => setForm({ ...form, trade_date: e.target.value })} />
            </div>
          </div>

          {form.trade_for === 'Close link' && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-21`}>{t('compliance.pad.closeLinkDetails')}</label>
              
              <input id={`${a11yId}-f-21`} style={s.input} value={form.close_link_details}
                onChange={e => setForm({ ...form, close_link_details: e.target.value })} />
            </div>
          )}

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-22`}>{t('compliance.pad.tradeType')}</label>
              
              <select id={`${a11yId}-f-22`} style={s.input} value={form.trade_type}
                onChange={e => setForm({ ...form, trade_type: e.target.value, unlisted_attestation: false })}>
                <option value=""></option>
                <option value="Listed market">{t('compliance.pad.listed')}</option>
                <option value="Unlisted / private investment">{t('compliance.pad.unlisted')}</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-23`}>{t('compliance.pad.security')}</label>
              
              <input id={`${a11yId}-f-23`} style={s.input} value={form.security}
                onChange={e => setForm({ ...form, security: e.target.value })} />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>{t('compliance.pad.direction')}</label>
            <div style={s.directionToggle}>
              {[['Buy', t('compliance.pad.buy')], ['Sell', t('compliance.pad.sell')]].map(([val, label]) => (
                <button key={val} style={{
                  ...s.dirBtn,
                  backgroundColor: form.direction === val ? (val === 'Buy' ? '#1a7a45' : '#c0392b') : '#fff',
                  color: form.direction === val ? '#fff' : '#333',
                  borderColor: val === 'Buy' ? '#1a7a45' : '#c0392b',
                }} onClick={() => setForm({ ...form, direction: val })}>{label}</button>
              ))}
            </div>
          </div>

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-24`}>{t('compliance.pad.currency')}</label>
              
              <select id={`${a11yId}-f-24`} style={s.input} value={form.currency}
                onChange={e => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-25`}>{t('compliance.pad.price')}</label>
              
              <input id={`${a11yId}-f-25`} style={s.input} type="number" min="0" step="0.0001" value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })} />
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-26`}>{t('compliance.pad.quantity')}</label>
            
            <input id={`${a11yId}-f-26`} style={s.input} type="number" min="0" step="1" value={form.quantity}
              onChange={e => setForm({ ...form, quantity: e.target.value })} />
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-27`}>{t('compliance.pad.confirmation')}</label>
            
            <input id={`${a11yId}-f-27`} type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => setConfirmFile(e.target.files[0] || null)}
              style={s.fileInput} />
            {confirmFile && <p style={s.fileName}>{confirmFile.name}</p>}
          </div>

          <div style={s.field}>
            <label style={s.label}>{t('compliance.pad.preApproval')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
              {PAD_PRE_APPROVAL_OPTIONS.map((opt, i) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#333', lineHeight: '1.5' }}>
                  <input type="radio" name="pre_approval_type" value={opt}
                    checked={form.pre_approval_type === opt}
                    onChange={() => setForm({ ...form, pre_approval_type: opt })}
                    style={{ marginTop: '3px', flexShrink: 0 }} />
                  {t(`compliance.pad.preApprovalOpt${i + 1}`)}
                </label>
              ))}
            </div>
          </div>

          {needsPreApproval && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-28`}>{t('compliance.pad.preApprovalFile')}</label>
              
              <input id={`${a11yId}-f-28`} type="file" accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setPreApprovalFile(e.target.files[0] || null)}
                style={s.fileInput} />
              {preApprovalFile && <p style={s.fileName}>{preApprovalFile.name}</p>}
            </div>
          )}

          {form.trade_type === 'Unlisted / private investment' && (
            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <input type="checkbox"
                  checked={form.unlisted_attestation}
                  onChange={e => setForm({ ...form, unlisted_attestation: e.target.checked })}
                  style={{ marginTop: '3px', flexShrink: 0 }} />
                {t('compliance.pad.unlistedAttestation')}
              </label>
            </div>
          )}

          {canChangeStatus && editingId && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-29`}>{t('compliance.statusLabel')}</label>
              
              <select id={`${a11yId}-f-29`} style={s.input} value={form.status || 'Pending'}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Pending">{t('compliance.status.pending')}</option>
                <option value="Approved">{t('compliance.status.approved')}</option>
                <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
              </select>
            </div>
          )}

          <div style={s.btnRow}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setEditingId(null); }}>{t('compliance.cancel')}</button>
            <button style={s.submitBtn} onClick={submit} disabled={saving}>
              {saving ? <><Spinner /> {t('compliance.saving')}</> : (editingId ? t('compliance.saveChanges') : t('common.submit'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <p style={s.empty}>{t('compliance.loading')}</p> : loadFailed ? (
        <LoadError message="Couldn't load these trades." onRetry={load} />
      ) : filtered.length === 0 ? (
        <p style={s.empty}>{t('compliance.noTrades')}</p>
      ) : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              {isAdmin && <th style={s.th}>{t('compliance.tableEmployee')}</th>}
              <th style={s.th}>{t('compliance.pad.tableDate')}</th>
              <th style={s.th}>{t('compliance.pad.tableSecurity')}</th>
              <th style={s.th}>{t('compliance.pad.tableDirection')}</th>
              <th style={s.th}>{t('compliance.pad.tableQty')}</th>
              <th style={s.th}>{t('compliance.pad.tablePrice')}</th>
              <th style={s.th}>{t('compliance.pad.tableCcy')}</th>
              <th style={s.th}>{t('compliance.pad.tableType')}</th>
              <th style={s.th}>{t('compliance.pad.tablePreApproval')}</th>
              {/* DECLDELAY: the submission date and the reporting delay, on the screen the
                  officer works in daily. Placed immediately BEFORE Status so the two read together
                  at the right-hand end of the row, after the declaration's own facts. Both stay
                  visible on a narrow screen: the table already scrolls horizontally in its own
                  container, so no column is dropped or collapsed at any width. */}
              <th style={s.th}>{t('compliance.tableSubmittedOn')}</th>
              <th style={{ ...s.th, whiteSpace: 'nowrap' }}>{t('compliance.tableDaysToReport')}</th>
              <th style={s.th}>{t('compliance.pad.tableStatus')}</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(rec => (
              <React.Fragment key={rec.id}>
                <tr style={s.tr}>
                  {isAdmin && (
                    <td style={s.td}>
                      {/* NAMERES: the shared resolver, never a raw id. */}
                      {resolveEmployeeName(employees, rec.employee_id, t, rec)}
                      {/* DECLENTITY: the canonical entity beneath, so two same-name employees
                          are distinguishable. Server-resolved; absent renders nothing. */}
                      <EntityLine entity={rec.entity} />
                    </td>
                  )}
                  <td style={s.td}>{fmt(rec.trade_date)}</td>
                  <td style={s.td}>
                    <span style={{ fontWeight: 500 }}>{rec.security || '-'}</span>
                    {rec.trade_for !== 'Self' && <span style={s.meta}><br />For: {rec.trade_for}{rec.close_link_details ? ` (${rec.close_link_details})` : ''}</span>}
                  </td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, backgroundColor: rec.direction === 'Buy' ? '#e8f8f0' : '#fde8e8', color: rec.direction === 'Buy' ? '#1a7a45' : '#c0392b' }}>
                      {rec.direction || '-'}
                    </span>
                  </td>
                  <td style={s.td}>{rec.quantity || '-'}</td>
                  <td style={s.td}>{rec.price || '-'}</td>
                  <td style={s.td}>{rec.currency || '-'}</td>
                  <td style={s.td}>
                    <span style={{ fontSize: '12px', color: rec.trade_type === 'Unlisted / private investment' ? '#92400E' : '#555' }}>
                      {rec.trade_type === 'Unlisted / private investment' ? 'Unlisted' : rec.trade_type || '-'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {/* PREAPPROVALCELL: the Pre-approval column showed "Please fill in all required
                        fields." on almost every row. CAUSE, established before changing anything: it
                        is a DISPLAY bug, not a data defect and not test data. The cell used
                        t('compliance.errRequired') as its fallback when no pre-approval FILE was
                        attached - and that key is the PAD form's own validation message, reused here
                        as if it were a value. Measured on dev: 8 of 9 PAD rows have an empty
                        pre_approval_type and none has a file, so almost every row hit the fallback
                        and rendered a sentence telling the reader to fill in a form they were only
                        looking at. Nothing is wrong with the records.
                        The column now shows the actual pre-approval value, the file name when one is
                        attached, and "-" when genuinely empty.
                        NOTED, NOT FIXED HERE: this cell was the only executable use of
                        compliance.errRequired, so the key is now orphaned. The three declaration forms
                        set their validation error from a hardcoded English string instead, which is a
                        separate untranslated-message defect outside this build. The key stays in both
                        locale files because it is the right translation for those call sites. */}
                    {rec.pre_approval_type === PAD_PRE_APPROVAL_OPTIONS[0]
                      ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('compliance.pad.preApprovalOpt1')}</span>
                      : <span style={{ fontSize: '12px', color: '#92400E' }}>
                          {rec.pre_approval_file_name
                            ? `${t('compliance.pad.uploadedPrefix', 'Uploaded')}: ${rec.pre_approval_file_name}`
                            : (rec.pre_approval_type || '-')}
                        </span>
                    }
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmt(rec.created_at)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtDelay(rec.days_to_report)}</td>
                  <td style={s.td}>{statusBadge(rec.status, t)}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {canChangeStatus && (
                      <button style={sM.viewBtn} onClick={() => setDetailsRec(rec)}>{t('compliance.view', 'View')}</button>
                    )}
                    {!readOnly && !canChangeStatus && rec.status === 'Pending' && (
                      <button style={{ ...s.editBtn, marginLeft: 4 }} onClick={() => startEdit(rec)}>{t('compliance.amend')}</button>
                    )}
                    {canChangeStatus && (
                      <select value={rec.status || 'Pending'}
                        onChange={e => handleStatusChange(rec, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginLeft: 4 }}>
                        <option value="Pending">{t('compliance.status.pending')}</option>
                        <option value="Approved">{t('compliance.status.approved')}</option>
                        <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
                      </select>
                    )}
                  </td>
                </tr>
                {rec.edit_history && rec.edit_history !== '[]' && (
                  <tr><td colSpan={isAdmin ? 13 : 12}><EditLog history={rec.edit_history} names={rec.edited_by_names} t={t} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table></ScrollableTable>
      )}
      {canChangeStatus && (
        <DeclarationDetailsModal
          rec={detailsRec}
          title={detailsRec ? (detailsRec.security || 'Personal Account Deal') : ''}
          subtitle={detailsRec ? resolveEmployeeName(employees, detailsRec.employee_id, t, detailsRec) : ''}
          rows={detailsRec ? [
            ['Trade for', detailsRec.trade_for],
            ['Close link details', detailsRec.close_link_details],
            ['Trade date', fmt(detailsRec.trade_date)],
            ['Trade type', detailsRec.trade_type],
            ['Security', detailsRec.security],
            ['Direction', detailsRec.direction],
            ['Quantity', detailsRec.quantity],
            ['Price', detailsRec.price],
            ['Currency', detailsRec.currency],
            ['Pre-approval type', detailsRec.pre_approval_type],
            ['Pre-approval document', <PadAttachmentLink key="pa" recordId={detailsRec.id} slot="preapproval" name={detailsRec.pre_approval_file_name} />],
            ['Unlisted attestation', _yesNo(detailsRec.unlisted_attestation)],
            ['Trade confirmation document', <PadAttachmentLink key="cf" recordId={detailsRec.id} slot="confirmation" name={detailsRec.confirmation_file_name} />],
            ['Submitted by', submitterName(detailsRec)],
          ] : []}
          onClose={() => setDetailsRec(null)}
        >
          {detailsRec && (
            <ReviewPanel declType="pad" rec={detailsRec}
              onReviewed={() => { setDetailsRec(null); load(); }} />
          )}
        </DeclarationDetailsModal>
      )}
    </div>
  );
}

// ─── Tab: Public Officials ───────────────────────────────

function PublicOfficialsTab({ entityScope, currentUser, employeeId, isAdmin, employees, readOnly, canChangeStatus }) {
  const { t } = useTranslation();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const showToast = useToast();
  // RESPONSIVE Phase 2: two-column field rows reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...s.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [detailsRec, setDetailsRec] = useState(null);  // COMPMODAL

  const blank = {
    engagement_date: '', means_of_engagement: '', location: '',
    official_names: '', position_link: '', purpose: '',
  };
  const [form, setForm] = useState(blank);

  // LOADERR: distinguishes a FAILED register load from a genuinely empty one.
  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    const params = isAdmin
      ? (filterEmp ? { employee_id: filterEmp } : {})
      : { employee_id: employeeId };
    axios.get(`${API}/compliance/public-officials`, { params })
      .then(res => {
        // COMPSCOPEUI: show only rows this account may actually reach. `employees` is the
        // already-scoped list, so a record whose employee is not in it is out of scope and
        // the backend would refuse it anyway. Unscoped (entityScope null) keeps every row,
        // so coo, admin and the CCO see exactly what they saw before.
        const rows = res.data || [];
        const allowed = new Set((employees || []).map(e => e.id));
        setRecords(entityScope ? rows.filter(r => allowed.has(r.employee_id)) : rows);
        setLoadFailed(false);
      })
      // LOADERR: a failed load must not render as "no engagements".
      .catch(() => { setRecords([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [isAdmin, employeeId, filterEmp, entityScope, employees]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (rec, newStatus) => {
    try {
      await axios.put(`${API}/compliance/public-officials/${rec.id}`, { status: newStatus, edited_by: currentUser });
      load();
    } catch (e) { showToast(fmtApiError(e), 'error'); }
  };

  const submit = async () => {
    if (!form.engagement_date || !form.means_of_engagement || !form.position_link || !form.purpose) {
      // ERRREQI18N: the locale key, which PREAPPROVALCELL left orphaned when it stopped misusing
      // it as a table-cell fallback. This is its real job: the form's own validation message.
      setError(t('compliance.errRequired')); return;
    }
    if (form.means_of_engagement === 'In Person' && !form.location) {
      setError('Location is required for in-person engagements.'); return;
    }
    setSaving(true); setError(null);
    try {
      const payload = { ...form, engagement_date: toISODate(form.engagement_date) };
      if (editingId) {
        await axios.put(`${API}/compliance/public-officials/${editingId}`, { ...payload, edited_by: currentUser });
      } else {
        await axios.post(`${API}/compliance/public-officials`, { ...payload, submitted_by: currentUser });
      }
      setShowForm(false); setEditingId(null); setForm(blank); load();
      // DECLMSGI18N: the SAME message as the PAD success toast, so it reads from the same key
      // rather than staying hardcoded English beside a translated twin.
      showToast(t('compliance.padSaved'), 'success');
    } catch (e) {
      setError(fmtApiError(e));
      showToast(fmtApiError(e), 'error');
    }
    setSaving(false);
  };

  const startEdit = (rec) => {
    setForm({
      engagement_date: rec.engagement_date || '',
      means_of_engagement: rec.means_of_engagement || '',
      location: rec.location || '',
      official_names: rec.official_names || '',
      position_link: rec.position_link || '',
      purpose: rec.purpose || '',
      status: rec.status || 'Pending',
    });
    setEditingId(rec.id); setShowForm(true);
  };

  return (
    <div>
      <div style={{ backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: '8px', padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
        <p style={{ margin: '0 0 8px 0' }}>{t('compliance.po.policy')}</p>
        <PolicyLink category="Anti-Bribery and Corruption" />
      </div>
      <div style={s.tabBar}>
        {isAdmin && (
          <select style={s.filterSelect} value={filterEmp}
            onChange={e => setFilterEmp(e.target.value)}>
            <option value="">{t('common.allEmployees')}</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        )}
        {!readOnly && !canChangeStatus && (
          <button style={s.newBtn} onClick={() => { setShowForm(true); setEditingId(null); setForm(blank); }}>
            {t('compliance.newEntry')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={s.formBox}>
          <div style={s.formTitleRow}>
            <h3 style={s.formTitle}>{editingId ? t('compliance.editEntry') : t('compliance.newEntry')}</h3>
            {!editingId && isTestAccount() && (
              <button style={s.demoBtn} onClick={() => setForm({
                engagement_date: todayISO(),
                means_of_engagement: 'Virtual',
                location: '',
                official_names: 'Kwame Mensah',
                position_link: 'Deputy Finance Minister, Ghana',
                purpose: 'Regulatory update meeting on capital markets',
              })}>{t('compliance.fillDemo')}</button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '0', marginBottom: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', fontSize: '13px' }}>
            <div style={{ backgroundColor: '#f0faf4', padding: '12px 14px', borderRight: '1px solid #e0e0e0' }}>
              <p style={{ fontWeight: 600, color: '#065F46', margin: '0 0 8px 0', fontSize: '12px', textTransform: 'uppercase' }}>{t('compliance.po.inScope')}</p>
              <ul style={{ margin: 0, paddingLeft: '16px', color: '#333', lineHeight: '1.7' }}>
                <li>{t('compliance.po.inScopeItem1')}</li>
                <li>{t('compliance.po.inScopeItem2')}</li>
              </ul>
            </div>
            <div style={{ backgroundColor: '#fef9f0', padding: '12px 14px' }}>
              <p style={{ fontWeight: 600, color: '#92400E', margin: '0 0 8px 0', fontSize: '12px', textTransform: 'uppercase' }}>{t('compliance.po.outScope')}</p>
              <ul style={{ margin: 0, paddingLeft: '16px', color: '#333', lineHeight: '1.7' }}>
                <li>{t('compliance.po.outScopeItem1')}</li>
                <li>{t('compliance.po.outScopeItem2')}</li>
                <li>{t('compliance.po.outScopeItem3')}</li>
              </ul>
            </div>
          </div>

          {error && <div style={s.error} role="alert" aria-live="polite">{error}</div>}

          <div style={rowStyle}>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-30`}>{t('compliance.po.engagementDate')}</label>
              
              <input id={`${a11yId}-f-30`} style={s.input} type="date" value={form.engagement_date}
                onChange={e => setForm({ ...form, engagement_date: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-31`}>{t('compliance.po.meansOfEngagement')}</label>
              
              <select id={`${a11yId}-f-31`} style={s.input} value={form.means_of_engagement}
                onChange={e => setForm({ ...form, means_of_engagement: e.target.value, location: '' })}>
                <option value=""></option>
                <option value="Virtual">{t('compliance.po.virtual')}</option>
                <option value="In Person">{t('compliance.po.inPerson')}</option>
              </select>
            </div>
          </div>

          {form.means_of_engagement === 'In Person' && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-32`}>{t('compliance.po.location')}</label>
              
              <input id={`${a11yId}-f-32`} style={s.input} value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                placeholder={t('compliance.po.locationPlaceholder')} />
            </div>
          )}

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-33`}>{t('compliance.po.officialNames')}</label>
            
            <input id={`${a11yId}-f-33`} style={s.input} value={form.official_names}
              onChange={e => setForm({ ...form, official_names: e.target.value })}
              placeholder={t('compliance.po.officialNamesPlaceholder')} />
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-34`}>{t('compliance.po.positionLink')}</label>
            
            <input id={`${a11yId}-f-34`} style={s.input} value={form.position_link}
              onChange={e => setForm({ ...form, position_link: e.target.value })}
              placeholder={t('compliance.po.positionLinkPlaceholder')} />
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-f-35`}>{t('compliance.po.purpose')}</label>
            
            <textarea id={`${a11yId}-f-35`} style={s.textarea} rows={3} value={form.purpose}
              onChange={e => setForm({ ...form, purpose: e.target.value })}
              placeholder={t('compliance.po.purposePlaceholder')} />
          </div>

          {canChangeStatus && editingId && (
            <div style={s.field}>
              <label style={s.label} htmlFor={`${a11yId}-f-36`}>{t('compliance.statusLabel')}</label>
              
              <select id={`${a11yId}-f-36`} style={s.input} value={form.status || 'Pending'}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Pending">{t('compliance.status.pending')}</option>
                <option value="Approved">{t('compliance.status.approved')}</option>
                <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
              </select>
            </div>
          )}

          <div style={s.btnRow}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setEditingId(null); }}>{t('compliance.cancel')}</button>
            <button style={s.submitBtn} onClick={submit} disabled={saving}>
              {saving ? <><Spinner /> {t('compliance.saving')}</> : (editingId ? t('compliance.saveChanges') : t('common.submit'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <p style={s.empty}>{t('compliance.loading')}</p> : loadFailed ? (
        <LoadError message="Couldn't load these engagements." onRetry={load} />
      ) : records.length === 0 ? (
        <p style={s.empty}>{t('compliance.noEngagements')}</p>
      ) : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              {isAdmin && <th style={s.th}>{t('compliance.tableEmployee')}</th>}
              <th style={s.th}>{t('compliance.po.tableDate')}</th>
              <th style={s.th}>{t('compliance.po.tableMeans')}</th>
              <th style={s.th}>{t('compliance.po.tableOfficials')}</th>
              <th style={s.th}>{t('compliance.po.tablePosition')}</th>
              <th style={s.th}>{t('compliance.po.tablePurpose')}</th>
              {/* DECLDELAY: the submission date and the reporting delay, on the screen the
                  officer works in daily. Placed immediately BEFORE Status so the two read together
                  at the right-hand end of the row, after the declaration's own facts. Both stay
                  visible on a narrow screen: the table already scrolls horizontally in its own
                  container, so no column is dropped or collapsed at any width. */}
              <th style={s.th}>{t('compliance.tableSubmittedOn')}</th>
              <th style={{ ...s.th, whiteSpace: 'nowrap' }}>{t('compliance.tableDaysToReport')}</th>
              <th style={s.th}>{t('compliance.po.tableStatus')}</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {records.map(rec => (
              <React.Fragment key={rec.id}>
                <tr style={s.tr}>
                  {isAdmin && (
                    <td style={s.td}>
                      {/* NAMERES: the shared resolver, never a raw id. */}
                      {resolveEmployeeName(employees, rec.employee_id, t, rec)}
                      {/* DECLENTITY: the canonical entity beneath, so two same-name employees
                          are distinguishable. Server-resolved; absent renders nothing. */}
                      <EntityLine entity={rec.entity} />
                    </td>
                  )}
                  <td style={s.td}>{fmt(rec.engagement_date)}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, backgroundColor: rec.means_of_engagement === 'Virtual' ? '#e8f0ff' : '#f0faf4', color: rec.means_of_engagement === 'Virtual' ? '#1a3a8e' : '#065F46' }}>
                      {rec.means_of_engagement || '-'}
                    </span>
                    {rec.location && <span style={s.meta}><br />{rec.location}</span>}
                  </td>
                  <td style={s.td}>{rec.official_names || '-'}</td>
                  <td style={s.td}>{rec.position_link || '-'}</td>
                  <td style={{ ...s.td, maxWidth: '200px' }}>{rec.purpose || '-'}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{fmt(rec.created_at)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtDelay(rec.days_to_report)}</td>
                  <td style={s.td}>{statusBadge(rec.status, t)}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {canChangeStatus && (
                      <button style={sM.viewBtn} onClick={() => setDetailsRec(rec)}>{t('compliance.view', 'View')}</button>
                    )}
                    {!readOnly && !canChangeStatus && rec.status === 'Pending' && (
                      <button style={{ ...s.editBtn, marginLeft: 4 }} onClick={() => startEdit(rec)}>{t('compliance.amend')}</button>
                    )}
                    {canChangeStatus && (
                      <select value={rec.status || 'Pending'}
                        onChange={e => handleStatusChange(rec, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginLeft: 4 }}>
                        <option value="Pending">{t('compliance.status.pending')}</option>
                        <option value="Approved">{t('compliance.status.approved')}</option>
                        <option value="Rejected">{t('compliance.status.rejected')}</option>
                <option value="Escalated">{t('compliance.status.escalated')}</option>
                      </select>
                    )}
                  </td>
                </tr>
                {rec.edit_history && rec.edit_history !== '[]' && (
                  <tr><td colSpan={isAdmin ? 10 : 9}><EditLog history={rec.edit_history} names={rec.edited_by_names} t={t} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table></ScrollableTable>
      )}
      {canChangeStatus && (
        <DeclarationDetailsModal
          rec={detailsRec}
          title={detailsRec ? (detailsRec.official_names || 'Public Official Engagement') : ''}
          subtitle={detailsRec ? resolveEmployeeName(employees, detailsRec.employee_id, t, detailsRec) : ''}
          rows={detailsRec ? [
            ['Engagement date', fmt(detailsRec.engagement_date)],
            ['Means of engagement', detailsRec.means_of_engagement],
            ['Location', detailsRec.location],
            ['Official names', detailsRec.official_names],
            ['Position / link to Enko', detailsRec.position_link],
            ['Purpose', detailsRec.purpose],
            ['Submitted by', submitterName(detailsRec)],
          ] : []}
          onClose={() => setDetailsRec(null)}
        >
          {detailsRec && (
            <ReviewPanel declType="public-officials" rec={detailsRec}
              onReviewed={() => { setDetailsRec(null); load(); }} />
          )}
        </DeclarationDetailsModal>
      )}
    </div>
  );
}

// ─── Tab: Attestations ────────────────────────────────────

const ATTEST_TYPE_LABEL = {
  outside_affiliations: 'Outside Affiliations',
  pad: 'Personal Account Dealing',
  geh: 'Gifts, Entertainment & Hospitality',
  public_officials: 'Public Officials',
};

const ATTEST_TAB_KEY = {
  outside_affiliations: 'outside_affiliations',
  pad: 'pad',
  geh: 'gifts',
  public_officials: 'public_officials',
};

function AttestationsTab({ entityScope, currentUser, employeeId, isAdmin, employees, readOnly, onNavigateTab }) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [attestations, setAttestations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);   // LOADERR3
  const [filterEmp, setFilterEmp] = useState('');
  const [confirmState, setConfirmState] = useState({});

  const targetId = isAdmin ? filterEmp : employeeId;

  // LOADERR3: this was the ONE genuinely silent catch left in this file - a failed attestation
  // fetch fell straight through to "No attestations found.", which is indistinguishable from an
  // employee who genuinely has none. The 4 register tabs above already track loadFailed
  // correctly; this brings the Attestations tab into line with them.
  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compliance/attestations`, { params: { employee_id: targetId } });
      setAttestations(res.data);
      setLoadFailed(false);
    } catch {
      setAttestations([]);
      setLoadFailed(true);
    }
    setLoading(false);
  }, [targetId]);

  useEffect(() => { load(); }, [load]);

  const stateFor = (id) => confirmState[id] || { checked: false, saving: false, error: null };

  const handleCheck = (id, checked) =>
    setConfirmState(prev => ({ ...prev, [id]: { ...stateFor(id), checked } }));

  const handleSubmit = async (id) => {
    setConfirmState(prev => ({ ...prev, [id]: { ...stateFor(id), saving: true, error: null } }));
    try {
      await axios.post(`${API}/compliance/attestations/${id}/complete`, { checkbox_confirmed: true });
      await load();
      setConfirmState(prev => ({ ...prev, [id]: { checked: false, saving: false, error: null } }));
      showToast('Attestation confirmed', 'success');
    } catch (e) {
      setConfirmState(prev => ({ ...prev, [id]: { ...stateFor(id), saving: false, error: fmtApiError(e) } }));
      showToast(fmtApiError(e), 'error');
    }
  };

  const pending   = attestations.filter(a => a.status === 'pending');
  const completed = attestations.filter(a => a.status === 'completed');

  const renderCard = (att) => {
    const isPending = att.status === 'pending';
    const cs = stateFor(att.id);
    const typeLabel = att.attestation_type in ATTEST_TYPE_LABEL
      ? t(`compliance.attestations.typeLabel.${att.attestation_type}`)
      : att.attestation_type;
    const canAttest = isPending && !readOnly && !isAdmin;
    return (
      <div key={att.id} style={{
        ...sA.card,
        borderLeft: isPending ? '4px solid #F59E0B' : '4px solid #10B981',
      }}>
        <div style={sA.cardHeader}>
          <div>
            <span style={sA.typeLabel}>{typeLabel}</span>
            <span style={sA.period}>{att.period}</span>
          </div>
          <span style={{
            ...s.badge,
            backgroundColor: isPending ? '#FEF3C7' : '#D1FAE5',
            color: isPending ? '#92400E' : '#065F46',
          }}>
            {isPending ? t('compliance.status.pending') : t('compliance.status.completed')}
          </span>
        </div>

        <p style={sA.attestText}>{t(`compliance.attestations.wording.${att.attestation_type}`, { defaultValue: att.attestation_text })}</p>

        {isPending && (
          <p style={sA.formLink}>
            {t('compliance.attestations.formLinkPrefix')}{' '}
            <button
              style={sA.linkBtn}
              onClick={() => onNavigateTab && onNavigateTab(ATTEST_TAB_KEY[att.attestation_type])}
            >
              {typeLabel}
            </button>{' '}
            {t('compliance.attestations.formLinkSuffix')}
          </p>
        )}

        {canAttest && (
          <div style={sA.checkRow}>
            <label style={sA.checkLabel}>
              <input
                type="checkbox"
                checked={cs.checked}
                onChange={e => handleCheck(att.id, e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              {t('compliance.attestations.checkboxConfirm')} <strong>{att.period}</strong>.
            </label>
          </div>
        )}

        {cs.error && <p style={{ ...s.error, marginTop: '10px' }} role="alert" aria-live="polite">{cs.error}</p>}

        {canAttest && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button
              style={{
                ...s.submitBtn,
                opacity: cs.checked ? 1 : 0.45,
                cursor: cs.checked ? 'pointer' : 'not-allowed',
              }}
              disabled={!cs.checked || cs.saving}
              onClick={() => handleSubmit(att.id)}
            >
              {cs.saving ? <><Spinner />{t('compliance.attestations.submitting')}</> : t('compliance.attestations.submitBtn')}
            </button>
          </div>
        )}

        {!isPending && att.attested_at && (
          <p style={sA.completedNote}>
            {/* ATTESTNOID: the "by <who>" mention was REMOVED (confirmed decision), and with it
                the raw employee_id it leaked. The old expression was
                `att.employee_id === employeeId ? currentUser : att.employee_id`, which had only
                two outcomes: the viewer's own email when looking at themselves, or an UNRESOLVED
                UUID for anybody else. In the compliance/admin view the rows are loaded for
                `targetId` (the employee picked in the dropdown) while the comparison was always
                against `employeeId` (the viewer), so the two differed by construction and the
                UUID was what actually rendered.
                Dropped rather than resolved to a name, because the attribution is redundant here:
                an attestation can only ever be completed by the employee it belongs to
                (ATTESTSELF, completion is self-only), and that employee is already named by the
                dropdown above. The DATE is kept, which is the part that carries information. */}
            {t('compliance.attestations.attestedOn')} {fmt(att.attested_at)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div>
      {isAdmin && (
        <div style={s.tabBar}>
          <select style={s.filterSelect} value={filterEmp} onChange={e => setFilterEmp(e.target.value)}>
            <option value="">{t('common.allEmployees')}</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        </div>
      )}

      {loading && <p style={s.empty}>{t('compliance.attestations.loading')}</p>}

      {/* LOADERR3: a genuine fetch failure, shown instead of the "no attestations" empty state.
          Same ordering the 4 register tabs use: loading, then failure, then empty. */}
      {!loading && loadFailed && (
        <LoadError message="Couldn't load these attestations." onRetry={load} />
      )}
      {!loading && !loadFailed && attestations.length === 0 && (
        <p style={s.empty}>{t('compliance.attestations.noAttestations')}</p>
      )}

      {!loading && pending.length > 0 && (
        <>
          <h3 style={sA.sectionTitle}>{t('compliance.attestations.sectionPending')}</h3>
          {pending.map(renderCard)}
        </>
      )}

      {!loading && completed.length > 0 && (
        <>
          <h3 style={{ ...sA.sectionTitle, marginTop: '28px' }}>{t('compliance.attestations.sectionCompleted')}</h3>
          {completed.map(renderCard)}
        </>
      )}
    </div>
  );
}

const sA = {
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: NAVY_FORM, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px 0' },
  card: { backgroundColor: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '18px 20px', marginBottom: '14px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  typeLabel: { fontSize: '15px', fontWeight: '600', color: NAVY_FORM, marginRight: '10px' },
  period: { fontSize: '13px', color: '#888', fontWeight: '500' },
  attestText: { fontSize: '13px', color: '#444', lineHeight: '1.6', margin: '0 0 12px 0', fontStyle: 'italic' },
  formLink: { fontSize: '12px', color: '#6B7280', margin: '0 0 12px 0' },
  linkBtn: { background: 'none', border: 'none', color: NAVY_FORM, fontWeight: '600', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: '0' },
  checkRow: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px 14px', marginTop: '4px' },
  checkLabel: { fontSize: '13px', color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '4px', lineHeight: '1.5' },
  completedNote: { fontSize: '12px', color: '#10B981', margin: '10px 0 0 0', fontWeight: '500' },
};

// ─── Main ComplianceModule component ─────────────────────

// ─── EMPDETAIL: one employee's whole compliance picture ───────────────────────
//
// The gap this fills: every compliance surface was organised BY TYPE first, so seeing one person
// meant visiting five tabs and re-selecting them on each, and EmployeeProfile deliberately never
// renders declarations at all. This is the by-PERSON view.
//
// STANDALONE and reachable only from inside this module (see canSeeEmployeeDetail), never from
// EmployeeProfile or any other shell - payroll and hr have no path to it.
//
// ONE request, not six: /compliance/employee-detail/{id} returns all five declaration types plus
// pending tasks together, so the screen cannot render a half picture that looks complete.
function ComplianceEmployeeDetail({ employees }) {
  const [employeeId, setEmployeeId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!employeeId) { setData(null); return undefined; }
    let live = true;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/compliance/employee-detail/${employeeId}`)
      .then(res => { if (live) setData(res.data || null); })
      .catch(e => {
        if (!live) return;
        const detail = e.response?.data?.detail;
        setErr(typeof detail === 'string' ? detail : 'Could not load this employee.');
        setData(null);
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [employeeId]);

  // Each declaration type renders through ONE shared section, so all five read identically and a
  // sixth type would need no new markup.
  const SECTIONS = [
    ['outside_affiliations', 'Outside Affiliations',
      r => [r.affiliated_entity, r.position, r.date_commenced].filter(Boolean).join(' - ')],
    ['gifts', 'Gifts and Entertainment',
      r => [r.entry_type, r.nature, r.firm_name,
            r.amount ? `${r.currency || ''} ${r.amount}`.trim() : null,
            r.date].filter(Boolean).join(' - ')],
    ['pad', 'Personal Account Dealing',
      r => [r.security, r.direction, r.trade_type, r.trade_date].filter(Boolean).join(' - ')],
    ['public_officials', 'Public Officials',
      r => [r.official_names, r.means_of_engagement, r.engagement_date].filter(Boolean).join(' - ')],
    ['attestations', 'Attestations',
      r => [r.attestation_type, r.period, r.attested_at].filter(Boolean).join(' - ')],
  ];

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label style={s.label} htmlFor="empdetail-pick">Employee</label>
        <select
          id="empdetail-pick"
          value={employeeId}
          onChange={e => setEmployeeId(e.target.value)}
          style={{ ...s.input, maxWidth: '420px' }}
        >
          <option value="">Select an employee...</option>
          {(employees || []).map(e => (
            <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
          ))}
        </select>
      </div>

      {loading && <p style={s.empty}>Loading...</p>}
      {err && <div style={s.error}>{err}</div>}

      {data && (
        <div>
          <div style={{ marginBottom: '18px' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: NAVY_FORM }}>
              {data.employee?.name}
            </h3>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {[data.employee?.entity, data.employee?.company_email].filter(Boolean).join(' · ')}
            </div>
          </div>

          {/* Pending compliance tasks FIRST: this is the actionable part, and the one thing that
              had no per-employee query anywhere before this screen. */}
          <div style={{ marginBottom: '18px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#666',
                         textTransform: 'uppercase' }}>
              Pending compliance tasks
            </h4>
            {(data.pending_tasks || []).length === 0 ? (
              <p style={s.empty}>No pending compliance tasks.</p>
            ) : (
              (data.pending_tasks || []).map(t => (
                <div key={t.id} style={{ fontSize: '13px', padding: '6px 0',
                                         borderBottom: '1px solid #eee' }}>
                  {t.title || t.task_type}
                  {t.due_date ? (
                    <span style={{ color: t.is_overdue ? '#c0392b' : '#888', marginLeft: '8px' }}>
                      due {t.due_date}{t.is_overdue ? ' (overdue)' : ''}
                    </span>
                  ) : null}
                </div>
              ))
            )}
          </div>

          {SECTIONS.map(([key, title, fmt]) => (
            <div key={key} style={{ marginBottom: '18px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#666',
                           textTransform: 'uppercase' }}>
                {title}
              </h4>
              {(data[key] || []).length === 0 ? (
                <p style={s.empty}>None recorded.</p>
              ) : (
                (data[key] || []).map(r => (
                  <div key={r.id} style={{ fontSize: '13px', padding: '6px 0',
                                           borderBottom: '1px solid #eee' }}>
                    {fmt(r) || '-'}
                    <span style={{ color: '#888', marginLeft: '8px' }}>{r.status}</span>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// EMPDETAILGONE: the tabs hidden from the compliance officer's own Declarations screen. At module
// scope so the fallback effect below has a stable reference. 'attestations' because she reaches
// attestations through the separate sidebar entry (DECLRENAME); 'employee_detail' because her
// compliance employee card is a strict superset of it.
const COMPLIANCE_HIDDEN_TABS = ['attestations', 'employee_detail'];

function ComplianceModule({ currentUser, isAdmin, readOnly = false, userRole = 'employee', isStaffView = false, onBack, initialTab }) {
  const { t } = useTranslation();
  // initialTab is optional and defaults to the existing 'outside_affiliations' landing tab -
  // every existing caller that doesn't pass it keeps today's exact behavior unchanged. Lets a
  // caller (e.g. a Tasks-list attestation click) land directly on a specific tab, most usefully
  // 'attestations', without touching this component's own tab UI or the AttestationsTab
  // submission flow at all.
  const [activeTab, setActiveTab] = useState(initialTab || 'outside_affiliations');
  // DECLRENAME: a compliance officer whose Attestations TAB is hidden must never be left sitting on
  // it. App.js now routes HER compliance Tasks-list attestation click to the sidebar Attestations
  // view instead of here, but a stale initialTab, a deep link or a browser restore could still
  // arrive carrying 'attestations', and the tab strip would then show no active tab and the body
  // nothing at all. Falling back to the landing tab keeps the screen coherent whatever the caller
  // passes. Employees and COO/admin keep the tab, so for them this never fires and
  // initialTab='attestations' still lands exactly where it always did.
  //
  // EMPDETAILGONE: 'employee_detail' joins 'attestations' in the same fallback, for the same reason
  // and in the same place rather than a second effect. No caller passes initialTab='employee_detail'
  // today (the only values ever passed are 'attestations' and undefined, both from EmployeeView),
  // but a stale deep link or a browser restore could still arrive carrying it, and the officer would
  // then see a tab strip with nothing active and an empty body. The render site is gated on
  // canSeeEmployeeDetail, so without this she would get that blank screen rather than the old tab.
  //
  // The list is the set of tabs HIDDEN FROM COMPLIANCE (COMPLIANCE_HIDDEN_TABS, at module scope so
  // it is a stable reference and needs no dependency exemption), so adding a future one means
  // adding it there and nowhere else.
  useEffect(() => {
    if (isStaffView && userRole === 'compliance' && COMPLIANCE_HIDDEN_TABS.includes(activeTab)) {
      setActiveTab('outside_affiliations');
    }
  }, [isStaffView, userRole, activeTab]);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  // FPTAB: whether the F&P questionnaire applies to this viewer, and whether theirs is still
  // outstanding. Both are REPORTED BY FPEmployeeSection rather than decided here, so the entity
  // rule and the "what counts as pending" rule each stay in the single place that owns them.
  // null means "not known yet": the tab stays hidden until the answer is a definite yes, so a
  // slow or failed eligibility check never flashes a tab at someone out of scope.
  const [fpApplicable, setFpApplicable] = useState(false);
  const [fpPending, setFpPending] = useState(false);
  // LOADERR: surfaced when the employee picker (admin) / self-record (employee) load fails.
  const [pickerFailed, setPickerFailed] = useState(false);
  // COMPSCOPEUI: the entity this account is restricted to, or null when unscoped. Read from the
  // SAME /auth/me the rest of the shell already calls, and resolved server side by the SAME
  // function the authorization uses, so the interface and the backend cannot disagree about who
  // is scoped.
  //
  // PRESENTATION ONLY: the backend remains the real gate. This decides what is OFFERED, never
  // what is permitted - a manually entered id is still refused with the same 403 as before.
  // Starts null (unscoped) and fails closed to null, so a failed lookup shows the full list
  // exactly as today rather than silently hiding records from someone entitled to see them.
  const [entityScope, setEntityScope] = useState(null);
  // DUALDECL: the employee ids the server's dual-entity exception adds to this account's scope
  // (the one dual-entity account, for a UK-scoped officer; empty for everyone else). Ids only,
  // never an address. Presentation only, like entityScope: the backend remains the real gate.
  const [scopeExtraIds, setScopeExtraIds] = useState([]);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/auth/me`)
      .then(res => {
        if (!live) return;
        setEntityScope(res.data?.compliance_entity_scope || null);
        setScopeExtraIds(Array.isArray(res.data?.compliance_scope_extra_ids)
          ? res.data.compliance_scope_extra_ids : []);
      })
      .catch(() => { if (live) { setEntityScope(null); setScopeExtraIds([]); } });
    return () => { live = false; };
  }, [currentUser]);

  useEffect(() => {
    // Staff/admin need the full employee list (for the picker); a regular employee only
    // needs their own id, fetched via the self endpoint (GET /employees/ is coo/admin/hr only).
    if (isAdmin) {
      axios.get(`${API}/employees/`)
        .then(res => { setEmployees(res.data); setPickerFailed(false); })
        // LOADERR: a broken employee picker must say so, not sit silently empty.
        .catch(() => setPickerFailed(true));
    } else {
      axios.get(`${API}/employees/me`)
        .then(res => { if (res.data?.id) { setEmployeeId(res.data.id); setPickerFailed(false); } })
        .catch(() => setPickerFailed(true));
    }
  }, [currentUser, isAdmin]);

  // COMPSCOPEUI: the employee list AS THE ACCOUNT MAY SEE IT. Mirrors the backend's own filter
  // exactly (Employee.primary_entity == scope), so a row offered here is a row the backend will
  // serve, and a row hidden here is one it would refuse. Unscoped returns the list untouched, so
  // coo, admin and the CCO notice no difference whatsoever.
  //
  // DUALDECL (2026-10-19, confirmed business decision): the UK officer now handles the
  // dual-entity account's DECLARATIONS as well as its attestations, so the ids the server's
  // exception adds (scopeExtraIds, from /auth/me) are included here. This supersedes the earlier
  // rule that deliberately left that account out of this picker. Nobody else is added: the list
  // holds that one account for a UK-scoped officer and is empty for everyone else.
  // SASCOPE: the scope may list several entities, comma separated; a single value matches as before.
  const scopeEntities = entityScope ? entityScope.split(',').map(s => s.trim()).filter(Boolean) : [];
  const scopedEmployees = entityScope
    ? (employees || []).filter(e => scopeEntities.includes(e.primary_entity) || scopeExtraIds.includes(e.id))
    : (employees || []);

  const canChangeStatus = isStaffView && userRole === 'compliance';

  // EMPDETAIL: the per-employee compliance screen is COMPLIANCE-STAFF ONLY. Gated on isStaffView
  // AND a compliance-family role, so it never appears in an employee's own compliance view (which
  // mounts this same module with isAdmin={false} to show them their own records), and never in
  // any other shell. payroll and hr hold neither condition, so the tab is not rendered and the
  // screen has no entry point for them at all.
  //
  // The BACKEND is the real control: /compliance/employee-detail/{id} applies _scope_employee_id,
  // the same helper the five declaration list endpoints use, so a non-privileged caller is
  // refused there regardless of what the UI offers.
  //
  // EMPDETAILGONE: the COMPLIANCE officer no longer gets this tab, because for her it is a strict
  // DUPLICATE. Her compliance employee card (Oversight > Employees) reads the SAME
  // /compliance/employee-detail/{id} and then adds regulatory classification, background check,
  // onboarding and attestations on top, so this tab shows her a subset of a screen she already has.
  // DECLRENAME established that superset relationship; this build acts on it.
  //
  // THE COO AND ADMIN KEEP IT, deliberately and unchanged: neither has the compliance employee
  // card, so for them this tab is not a duplicate of anything. The COO's own EmployeeProfile is a
  // different screen with a different purpose and does not carry the five declaration registers.
  // Removing the tab for them would delete a capability rather than a duplicate.
  //
  // The SAME condition as the hidden Attestations tab, `isStaffView && userRole === 'compliance'`,
  // so the two exclusions cannot drift apart.
  const canSeeEmployeeDetail = Boolean(
    isStaffView && ['coo', 'admin'].includes(userRole));

  // FPTAB: the Fitness & Propriety questionnaire, as a tab of THIS page rather than a separate
  // block stacked above it.
  //
  // WHAT THIS REPLACES. FPEmployeeSection used to render in its own card in EmployeeView, ABOVE
  // this module - above its "Back to dashboard" button, its header and its tabs, in its own
  // typography. It read as two screens stacked on one another. It is the employee's own compliance
  // obligation, so it belongs beside their other ones.
  //
  // THE EMPLOYEE VIEW ONLY, which is what `isFpViewer` means. employeeId is set by the effect
  // above ONLY on the `isAdmin === false` branch, from /employees/me, so it is the viewing
  // account's OWN record and is null in every staff mount. The compliance, COO, admin and HR
  // views therefore gain no tab and lose nothing, which this build must not change. The staff
  // side of the F&P process (manager review, HR review, compliance outcome) lives in its own
  // components and is untouched.
  //
  // ELIGIBILITY IS NOT RE-DERIVED HERE. FPEmployeeSection already asks the backend
  // (/fp-assessment/eligible/check) and renders nothing when the answer is not a definite yes, so
  // the entity rule stays in the one place that owns it. The tab BUTTON is gated on the same
  // answer, reported up by onApplicable, so an out-of-scope employee never sees an empty tab.
  const isFpViewer = Boolean(!isAdmin && !isStaffView && employeeId);
  // FPHIDE: this page asks the server ITSELF whether F&P is switched on, on every load (the
  // answer is never cached), instead of trusting only what the questionnaire reports up. Starts
  // false and a failed request stays false, so F&P is hidden unless the server says a definite
  // yes. While off: employees get no tab and no badge whatever record exists; coo, admin and
  // compliance get the tab (the notice inside) and never the badge.
  const [fpOn, setFpOn] = useState(false);
  // FPBUTTON: whether the server has answered yet, so a deep link to the F&P tab can fall back only
  // once the answer is known rather than on the initial false.
  const [fpChecked, setFpChecked] = useState(false);
  useEffect(() => {
    if (!isFpViewer) return undefined;
    let live = true;
    axios.get(`${API}/fp-assessment/status/enabled`)
      .then(r => { if (live) { setFpOn(Boolean(r && r.data && r.data.enabled === true)); setFpChecked(true); } })
      .catch(() => { if (live) { setFpOn(false); setFpChecked(true); } });
    return () => { live = false; };
  }, [isFpViewer]);
  const fpNoticeRole = FP_NOTICE_ROLES.includes(userRole);
  const showFpTab = isFpViewer && fpApplicable && (fpOn || fpNoticeRole);
  // FPBUTTON: arriving on the F&P tab (the F&P email's link) while F&P is off for this viewer lands
  // on the page's usual first tab instead of an empty one.
  //
  // FPFIX2: the same when F&P is on but this person has no assessment to complete (out of scope),
  // once the questionnaire has finished asking (fpSettled); and in both cases the page now SAYS so
  // above the first tab, instead of leaving the person to wonder where the link went.
  const [fpSettled, setFpSettled] = useState(false);
  const [fpLinkNotice, setFpLinkNotice] = useState(false);
  useEffect(() => {
    if (activeTab !== 'fp_assessment' || !fpChecked) return;
    if (!(fpOn || fpNoticeRole) || (fpSettled && !fpApplicable)) {
      setActiveTab('outside_affiliations');
      setFpLinkNotice(true);
    }
  }, [activeTab, fpChecked, fpOn, fpNoticeRole, fpSettled, fpApplicable]);

  // DECLRENAME: this screen is the DECLARATIONS screen for compliance staff, and the compliance
  // sidebar has its own Attestations entry alongside it, so the Attestations TAB here duplicated a
  // view one click away. It is hidden for that one audience and for no other.
  //
  // WHY THE CONDITION IS THIS EXACT PAIR AND NOT `userRole === 'compliance'` ALONE. The same
  // component serves four callers, and the compliance officer reaches it through two of them: as
  // compliance STAFF (App.js passes isStaffView) and as an EMPLOYEE looking at her own records
  // (App.js's other call site, and EmployeeView's My Profile, neither of which passes it). Keying
  // on the role alone would have taken the tab away from her own profile too, where the
  // checkbox-confirm card is the ONLY way she completes her own attestations. The pair is the
  // established seam in this component - canChangeStatus and canSeeEmployeeDetail above both read
  // isStaffView together with the role for the same reason.
  //
  // COO, ADMIN AND EVERY EMPLOYEE KEEP THE TAB UNCHANGED. The COO/admin dashboard call
  // (Dashboard.js, readOnly) passes no isStaffView, and ATTESTSELF's completion control is
  // untouched: this hides a tab, it does not alter what the tab does for anyone who still sees it.
  const hideAttestationsTab = isStaffView && userRole === 'compliance';

  const tabs = [
    { key: 'outside_affiliations', label: t('compliance.tabs.outsideAffiliations') },
    { key: 'gifts', label: t('compliance.tabs.gifts') },
    { key: 'pad', label: t('compliance.tabs.pad') },
    { key: 'public_officials', label: t('compliance.tabs.publicOfficials') },
    ...(hideAttestationsTab
      ? []
      : [{ key: 'attestations', label: t('compliance.tabs.attestations') }]),
    // FPTAB: appended, like EMPDETAIL below, so every existing tab keeps its position.
    ...(showFpTab
      ? [{
          key: 'fp_assessment',
          label: t('compliance.tabs.fitnessPropriety', 'Fitness & Propriety'),
          // FPTAB: the pending indicator. An assessment the employee still has to complete is
          // worth saying so on the tab itself, because this page is where they would come looking.
          // FPHIDE: never while F&P is switched off, for any role.
          badge: fpOn && fpPending,
        }]
      : []),
    // EMPDETAIL: appended rather than inserted, so every existing tab keeps its position.
    ...(canSeeEmployeeDetail
      ? [{ key: 'employee_detail', label: t('compliance.tabs.employeeDetail', 'Employee detail') }]
      : []),
  ];

  return (
    <div style={s.container}>
      {onBack && (
        <button
          style={s.backBtn}
          onClick={onBack}
          onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
        >
          ← Back to dashboard
        </button>
      )}
      <div style={s.header}>
        {/* DECLRENAME: compliance staff see "Declarations" / "Declarations" in French, matching the
            renamed sidebar entry, so this screen reads as the declarations register sitting next to
            the separate Attestations entry. Every other audience keeps the existing
            compliance.title, so the COO, admin and employee views are untouched. */}
        <h1 style={s.title}>
          {hideAttestationsTab ? t('compliance.declarationsTitle') : t('compliance.title')}
        </h1>
        <p style={s.subtitle}>{isAdmin ? t('common.allEmployees') : currentUser}</p>
      </div>

      {/* LOADERR: failed picker/self-record load - the tabs below depend on it. */}
      {pickerFailed && (
        <div style={{ margin: '0 0 12px 0' }}>
          <LoadError message={isAdmin ? "Couldn't load the employee list." : "Couldn't load your employee record."} />
        </div>
      )}

      {fpLinkNotice && (
        <div role="status" data-testid="fp-link-notice"
             style={{ margin: '0 0 12px 0', fontSize: '13px', border: '1px solid #e2e6ec', borderRadius: '8px', padding: '10px 12px' }}>
          {t('fp.linkNoAssessment')}
        </div>
      )}

      <div style={s.tabs}>
        {tabs.map(tab => (
          <button key={tab.key}
            style={{ ...s.tab, ...(activeTab === tab.key ? s.tabActive : {}) }}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
            {/* FPTAB: a tab carrying outstanding work says so. Rendered only when the tab itself
                declares a badge, so every other tab's markup is byte-identical to before. */}
            {tab.badge && <span style={s.tabBadge}>{t('compliance.tabs.due', 'Due')}</span>}
          </button>
        ))}
      </div>

      <div style={s.tabContent}>
        {activeTab === 'outside_affiliations' && (
          <OutsideAffiliationsTab entityScope={entityScope} currentUser={currentUser} employeeId={employeeId} isAdmin={isAdmin} employees={scopedEmployees} readOnly={readOnly} canChangeStatus={canChangeStatus} />
        )}
        {activeTab === 'gifts' && (
          <GiftsTab entityScope={entityScope} currentUser={currentUser} employeeId={employeeId} isAdmin={isAdmin} employees={scopedEmployees} readOnly={readOnly} canChangeStatus={canChangeStatus} />
        )}
        {activeTab === 'pad' && (
          <PADTab entityScope={entityScope} currentUser={currentUser} employeeId={employeeId} isAdmin={isAdmin} employees={scopedEmployees} readOnly={readOnly} canChangeStatus={canChangeStatus} />
        )}
        {activeTab === 'public_officials' && (
          <PublicOfficialsTab entityScope={entityScope} currentUser={currentUser} employeeId={employeeId} isAdmin={isAdmin} employees={scopedEmployees} readOnly={readOnly} canChangeStatus={canChangeStatus} />
        )}
        {activeTab === 'attestations' && (
          <AttestationsTab entityScope={entityScope} currentUser={currentUser} employeeId={employeeId} isAdmin={isAdmin} employees={scopedEmployees} readOnly={readOnly} onNavigateTab={setActiveTab} />
        )}
        {/* FPTAB: the gate is repeated here, not just on the tab button, for the same reason
            EMPDETAIL repeats its own: a tab that is merely unrendered can still be reached by
            whatever else sets activeTab.

            MOUNTED EVEN WHEN THE TAB IS NOT ACTIVE, and hidden with CSS instead. The component is
            what knows whether the questionnaire applies and whether it is outstanding, and it
            learns that by asking the backend on mount. Mounting it only on activation would mean
            the tab could never appear in the first place, because nothing would have asked.
            Hidden rather than unmounted so switching tabs does not discard a part-filled
            questionnaire the employee has not submitted yet. */}
        {isFpViewer && (
          <div style={{ display: activeTab === 'fp_assessment' && showFpTab ? 'block' : 'none' }}>
            <FPEmployeeSection
              employeeId={employeeId}
              onApplicable={setFpApplicable}
              onPending={setFpPending}
              onSettled={setFpSettled}
              viewerRole={userRole}
            />
          </div>
        )}
        {/* EMPDETAIL: the gate is repeated here, not just on the tab button. A tab that is merely
            unrendered can still be reached by whatever else sets activeTab; gating the PANEL too
            means an unauthorized role cannot render this screen by any route through this
            component. */}
        {activeTab === 'employee_detail' && canSeeEmployeeDetail && (
          <ComplianceEmployeeDetail employees={scopedEmployees} />
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────

const s = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '1100px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  // Prominent bordered treatment (matches NewStarterForm.js/SponsorForm.js/LeaverForm.js's
  // established "Back to dashboard" button style - BACKBTN-1), not the earlier low-visibility
  // plain-text link.
  backBtn: {
    background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: NAVY_FORM,
    fontSize: '14px', cursor: 'pointer', padding: '9px 18px',
    borderRadius: '6px', marginBottom: '24px', fontWeight: '600',
  },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  subtitle: { fontSize: '14px', color: '#888', margin: '4px 0 0 0' },
  tabs: { display: 'flex', gap: '0', marginBottom: '24px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
  tab: { flex: 1, padding: '14px 16px', border: 'none', background: 'none', fontSize: '14px', fontWeight: '500', color: '#888', cursor: 'pointer', borderBottom: '3px solid transparent', transition: 'all 0.15s' },
  tabActive: { color: NAVY_FORM, borderBottom: `3px solid ${NAVY_FORM}`, backgroundColor: '#f8f9fa' },
  tabContent: { backgroundColor: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  // FPTAB: the "Due" pill on a tab carrying outstanding work. Uses the same gold the rest of the
  // portal uses for a pending state, at a size that reads as a marker beside the label rather
  // than competing with it.
  tabBadge: {
    display: 'inline-block', marginLeft: '7px', padding: '1px 7px', borderRadius: '10px',
    backgroundColor: '#fdf8ee', color: '#7a4d00', border: '1px solid #e8dfc0',
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.03em', verticalAlign: 'middle',
  },

  tabBar: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  filterSelect: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' },
  filterInput: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' },
  newBtn: { backgroundColor: NAVY_FORM, color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  formBox: { backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '24px' },
  formTitle: { fontSize: '16px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  formTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  demoBtn: { padding: '5px 12px', border: '1px solid #9ca3af', borderRadius: '5px', background: '#fff', fontSize: '12px', color: '#6b7280', cursor: 'pointer', fontWeight: '500' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', resize: 'vertical' },
  fileInput: { fontSize: '13px', color: '#333', marginTop: '4px' },
  fileName: { fontSize: '12px', color: '#27ae60', margin: '6px 0 0 0', fontWeight: '500' },
  directionToggle: { display: 'flex', gap: '0' },
  dirBtn: { flex: 1, padding: '9px 0', border: '1px solid #ddd', fontSize: '14px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.15s' },
  spouseToggle: { background: 'none', border: 'none', color: NAVY_FORM, fontSize: '13px', cursor: 'pointer', fontWeight: '500', padding: '0 0 12px 0', display: 'block' },
  spouseBox: { backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: '8px', padding: '16px', marginBottom: '16px' },
  spouseTitle: { fontSize: '14px', fontWeight: '600', color: NAVY_FORM, margin: '0 0 14px 0' },
  btnRow: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn: { padding: '9px 20px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer', color: '#333' },
  submitBtn: { padding: '9px 20px', border: 'none', borderRadius: '6px', backgroundColor: NAVY_FORM, color: '#fff', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  error: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#c0392b', marginBottom: '14px' },
  empty: { color: '#888', fontStyle: 'italic', fontSize: '14px', padding: '20px 0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', fontSize: '12px', color: '#666', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #eee' },
  td: { padding: '10px 12px', fontSize: '13px', color: '#333', verticalAlign: 'top' },
  meta: { fontSize: '11px', color: '#aaa' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '500' },
  editBtn: { padding: '4px 12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff', fontSize: '12px', cursor: 'pointer', color: '#333', whiteSpace: 'nowrap' },
  editLog: { backgroundColor: '#fafafa', borderLeft: '3px solid #ddd', padding: '10px 14px', margin: '0 12px 8px 12px' },
  editLogTitle: { fontSize: '11px', fontWeight: '600', color: '#888', margin: '0 0 6px 0', textTransform: 'uppercase' },
  editLogEntry: { marginBottom: '6px' },
  editLogMeta: { fontSize: '12px', color: '#555', display: 'block', marginBottom: '2px' },
  editLogChange: { fontSize: '12px', color: '#777', display: 'block', paddingLeft: '12px' },
  spinner: { display: 'inline-block', width: '12px', height: '12px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.65s linear infinite', marginRight: '6px', verticalAlign: 'middle' },
  fieldNote: { fontSize: '12px', color: '#b7860b', margin: '5px 0 0 0', fontStyle: 'italic' },
  toggleRow: { display: 'flex', gap: '0' },
  toggleBtn: { padding: '9px 24px', border: `1px solid ${NAVY_FORM}`, fontSize: '14px', cursor: 'pointer', transition: 'all 0.15s' },
};

export default ComplianceModule;
